import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { SECONDARY_MUSCLES } from './constants/exerciseCategories';
import { toastSuccess, toastError } from './ui/toast';
import ConfirmDialog from './ui/ConfirmDialog';
import { getWeightHistory, getUserCheckIns, getStaffPhotoUrls } from './services/checkInService';
import { getStrengthProgressSummary } from './services/oneRmService';
import { getCoachNotes, addCoachNote } from './services/coachNotesService';
import { getEffectiveWeight, getSetVolume } from './utils/weightUtils';
import { generateCoachBrief, getEffectiveBriefingWeeks, describeFood, guardFoodBeforeSend } from './utils/performanceBriefing';
import { deleteClientCopyProgram } from './services/programCloneService';
import { assignProgramToClient } from './services/programAssignService';
import { loadClientSwaps, summariseSwaps, structureSwaps, swapHistorySince } from './services/swapService';
import { loadClientFoodSummary } from './services/foodSummaryService';
import { loadWeeklySessionTarget } from './services/weeklyTargetService';
import { groupAssignablePrograms } from './utils/assignablePrograms';
import { generateNextWeek } from './services/weekGeneratorService';
import { setClientWeek } from './services/clientWeekService';
import {
  updateProgramExerciseCascade,
  deleteProgramExerciseCascade,
  editorUpdateBlockItem,
  editorDeleteBlockItem,
  editorUpdateBlockRest,
  editorAddExercise,
  editorRefusal,
  editorAddDay,
} from './services/programCascadeService';
import { getDefaultPrescription } from './utils/exerciseDefaults';
import ClientCheckInsTab from './components/ClientCheckInsTab';
import ClientNutritionTab from './components/ClientNutritionTab';
import ClientPerformanceTab from './components/ClientPerformanceTab';
import ClientProgramsTab from './components/ClientProgramsTab';
import ClientMessagesTab from './components/ClientMessagesTab';
import ClientListView from './components/ClientListView';
import ClientDetailHeader from './components/ClientDetailHeader';
import { getLifecycleBucket, getInactiveReason } from './utils/lifecycle';
import { getMessages, sendMessage as sendMessageApi, markMessagesRead, getUnreadCountsForClients } from './services/messagesService';
import { styles } from './styles/clientManagementStyles';

// Client list is paginated — this is the page size, not a hard cap. loadData() re-fetches
// everything with a growing limit each time "Load More" is clicked, rather than trying to
// incrementally merge partial state (sessions/assignments/coach-map/metrics are all computed
// together from the full loaded set, and doing that consistently on a delta-only load would
// risk subtle staleness bugs in a file this central to daily coach usage).
const CLIENT_PAGE_SIZE = 100;

// ── Local mirrors of block_items / program_exercises cascade writes ──────────
// After a swap or delete, the DB write is already done; these apply the exact
// same transformation to the in-memory programDetails tree so the editor doesn't
// have to fire a full 6-query loadProgramDetails refetch on every single edit
// (that refetch, repeated per edit during a review, was piling concurrent
// requests onto Supabase's connection pool — the source of the 500s seen in the
// console). Match semantics mirror the SQL exactly: the shown row always
// (by primary key), plus every matching row in the forward-week blocks/days the
// cascade targeted.
const patchBlockItemsTree = (programDetails, { itemId, blockIds, oldExerciseId, transform }) => {
  const blockIdSet = blockIds ? new Set(blockIds) : null;
  return (programDetails || []).map(week => ({
    ...week,
    days: (week.days || []).map(day => ({
      ...day,
      blocks: (day.blocks || []).map(block => {
        const blockTargeted = blockIdSet ? blockIdSet.has(block.id) : false;
        return {
          ...block,
          items: (block.items || [])
            .map(item => {
              const isShownRow = itemId != null && item.id === itemId;
              const isCascadeRow = blockTargeted && oldExerciseId != null && item.exercise_id === oldExerciseId;
              return (isShownRow || isCascadeRow) ? transform(item) : item;
            })
            .filter(Boolean),
        };
      }),
    })),
  }));
};

const patchLegacyExercisesTree = (programDetails, { peId, dayIds, oldExerciseId, transform }) => {
  const dayIdSet = dayIds ? new Set(dayIds) : null;
  return (programDetails || []).map(week => ({
    ...week,
    days: (week.days || []).map(day => {
      const dayTargeted = dayIdSet ? dayIdSet.has(day.id) : false;
      return {
        ...day,
        exercises: (day.exercises || [])
          .map(pe => {
            const isShownRow = peId != null && pe.id === peId;
            const isCascadeRow = dayTargeted && oldExerciseId != null && pe.exercise_id === oldExerciseId;
            return (isShownRow || isCascadeRow) ? transform(pe) : pe;
          })
          .filter(Boolean),
      };
    }),
  }));
};

// Compute dashboard metrics for each client
const computeClientMetrics = (clients, sessions, activeSessionIds = new Set(), assignments = []) => {
  const now = new Date();
  const metrics = {};

  clients.forEach(client => {
    try {
      // Find the start date of the current active program assignment.
      // Sessions from before that date belong to a replaced program and should
      // not count against the completion rate.
      const currentAssignment = assignments.find(
        a => a.user_id === client.user_id && a.is_active
      );
      const programStartDate = currentAssignment?.assigned_at
        ? new Date(currentAssignment.assigned_at)
        : null;

      const userSessions = sessions.filter(s => {
        if (s.user_id !== client.user_id) return false;
        if (programStartDate && s.started_at) {
          return new Date(s.started_at) >= programStartDate;
        }
        return true;
      });
      const completedSessions = userSessions.filter(s => activeSessionIds.has(s.id));

      // Completion rate (last 30 days)
      const completionRate = userSessions.length > 0
        ? Math.round((completedSessions.length / userSessions.length) * 100)
        : null;

      // Last activity date (completed_at preferred, started_at as fallback)
      const sortedCompleted = completedSessions
        .filter(s => s.completed_at || s.started_at)
        .sort((a, b) => new Date(b.completed_at || b.started_at) - new Date(a.completed_at || a.started_at));
      const lastCompletedDate = sortedCompleted[0]?.completed_at || sortedCompleted[0]?.started_at || null;

      // Total workouts in last 30 days
      const totalWorkouts = completedSessions.length;

      // Calculate flags
      const flags = [];

      // Flag: No session in 7 days
      if (lastCompletedDate) {
        const lastDate = new Date(lastCompletedDate);
        if (!isNaN(lastDate)) {
          const daysSinceLastSession = Math.floor(
            (now - lastDate) / (1000 * 60 * 60 * 24)
          );
          if (daysSinceLastSession >= 7) {
            flags.push({
              type: 'inactive',
              label: `${daysSinceLastSession}d inactive`,
              severity: 'danger',
            });
          }
        }
      } else if (userSessions.length > 0) {
        flags.push({
          type: 'inactive',
          label: 'Never completed',
          severity: 'danger',
        });
      }

      // Flag: Low completion rate
      if (completionRate !== null && completionRate < 50 && userSessions.length >= 3) {
        flags.push({
          type: 'lowCompletion',
          label: `${completionRate}% completion`,
          severity: 'warning',
        });
      }

      metrics[client.user_id] = {
        completionRate,
        lastCompletedDate,
        totalWorkouts,
        totalSessions: userSessions.length,
        flags,
        recentSessions: sortedCompleted.slice(0, 10),
      };
    } catch (err) {
      // Skip clients with bad data rather than crashing the whole view
      metrics[client.user_id] = {
        completionRate: null,
        lastCompletedDate: null,
        totalWorkouts: 0,
        totalSessions: 0,
        flags: [],
        recentSessions: [],
      };
    }
  });

  return metrics;
};

// Additions whose answer never came, by programme, day and exercise: choosing the same again reuses the
// request id, so it lands once whatever happened to the first (Codex's reviews 024 R3 and 025 F3). Kept for
// as long as the page is open, through closing the editor and opening another client.
const unansweredAdds = new Map();

const ClientManagement = ({ onBack, onEditProgram, initialClientId, initialTab, needs }) => {
  const [clients, setClients] = useState([]);
  const [clientTab, setClientTab] = useState('active'); // 'active' | 'inactive'
  const [archivingClient, setArchivingClient] = useState(null); // user_id being archived/restored
  const [coaches, setCoaches] = useState({});  // ✅ Store coach info by ID
  const [programs, setPrograms] = useState([]);
  // Who is signed in, and who built the programs that are not theirs — both only for grouping the
  // "assign a program" list.
  const [signedInCoach, setSignedInCoach] = useState({ authId: null, role: null });
  const [programBuilders, setProgramBuilders] = useState({});
  const [programAssignments, setProgramAssignments] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientGlp1, setClientGlp1] = useState(null);
  const [clientNutritionData, setClientNutritionData] = useState(null);
  const [nutritionStatus, setNutritionStatus] = useState('loading');
  const [nutritionRetry, setNutritionRetry] = useState(0);
  const [assigningTo, setAssigningTo] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [editingProgram, setEditingProgram] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  // Snapshot of the last full loadProgramDetails() result, keyed by program id.
  // Lets re-opening an unmodified program paint instantly with no network call.
  // Any structural mutation (swap/delete/add/week-advance) drops the entry so a
  // stale tree is never served — worst case is one clean refetch on next open.
  const programDetailsCacheRef = useRef({});
  // The latest program asked for: an answer for an earlier one is dropped (Codex's review 023, R3)
  const programLoadRef = useRef(0);
  const [programLoadFailed, setProgramLoadFailed] = useState(false);
  // The week the editor shows: the client's current week, read fresh when the program opens (Codex's
  // review 023, R1). { week, note } — `week` null when there is no week to edit.
  const [editorWeek, setEditorWeek] = useState({ week: null, note: null });
  const [addDaySaving, setAddDaySaving] = useState(false);
  // The program open in the editor now. A save, a refusal or an addition that answers after the coach
  // opened another program or closed the editor must not reload or change it (Codex's review 024, R4).
  const editingProgramRef = useRef(null);
  const [removingExercise, setRemovingExercise] = useState(false);
  const [swappingExercise, setSwappingExercise] = useState(null); // { peId, currentExercise }
  const [pendingSwapEx, setPendingSwapEx] = useState(null); // { id, name } — selected but not yet confirmed
  const [swapReason, setSwapReason] = useState('');
  // Guards swapBlockExercise/swapExercise against concurrent duplicate requests — a real incident
  // (26 Aug 2026) traced 9 identical PATCH /block_items retries in under a minute back to repeated
  // clicks on "Confirm swap" while the first request was still hanging, since nothing disabled the
  // button or ignored the re-entrant call while a save was already in flight.
  const [swapSaving, setSwapSaving] = useState(false);
  const [clientCheckIns, setClientCheckIns] = useState([]);
  const [clientWeightHistory, setClientWeightHistory] = useState([]);
  const [clientStartingWeight, setClientStartingWeight] = useState(null);
  const [clientOnboardingPhotos, setClientOnboardingPhotos] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, programId: null, userId: null });
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, client: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [removeExerciseDialog, setRemoveExerciseDialog] = useState({ isOpen: false, action: null });
  const [clientMetrics, setClientMetrics] = useState({});
  const [activeTab, setActiveTab] = useState('performance');
  // Opened from "Needs you today": the tab with the job ('messages' or 'checkins'), applied once
  const pendingTabRef = useRef(initialTab || null);
  const [performanceAnalytics, setPerformanceAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [timeRange, setTimeRange] = useState(12);
  const [analyticsDateSpan, setAnalyticsDateSpan] = useState('');
  const [briefingText, setBriefingText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [clientCoachNotes, setClientCoachNotes] = useState([]);
  const [activePieIndex, setActivePieIndex] = useState(null);
  const [programHistory, setProgramHistory] = useState([]);
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [showAdvanceWeek, setShowAdvanceWeek] = useState(false);
  const [advanceWeekTarget, setAdvanceWeekTarget] = useState('');
  const [advancingWeek, setAdvancingWeek] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [generatingNextWeek, setGeneratingNextWeek] = useState(false);
  const [loadingProgramHistory, setLoadingProgramHistory] = useState(false);
  const [exerciseAvgs, setExerciseAvgs] = useState({}); // keyed by exercise_id
  const [swapSearch, setSwapSearch] = useState('');
  const [addingExercise, setAddingExercise] = useState(null); // { dayId, dayNumber, isBlock }
  // Guards addExerciseToDay against concurrent duplicate inserts — same class of bug as the
  // 26 Aug 2026 swap incident (see swapSaving below): the exercise-picker row is a plain
  // clickable <div>, not a <button disabled>, and addExerciseToDay does a multi-step insert
  // (+ cascade to future weeks) with no guard, so a rapid double-click could create duplicate
  // exercise rows instead of just a duplicate no-op write.
  const [addExerciseSaving, setAddExerciseSaving] = useState(false);
  const [addExerciseSearch, setAddExerciseSearch] = useState('');
  const [showSwapAll, setShowSwapAll] = useState(false);
  const [clientFlags, setClientFlags] = useState([]); // array of unresolved flag objects for selected client
  // The selected client's in-workout swaps, grouped by the exercise swapped away from (summariseSwaps).
  // null means the load failed, which the panel says rather than showing "no swaps".
  const [clientSwaps, setClientSwaps] = useState([]);
  const swapsRequestRef = useRef(null); // the client the latest swaps request was for
  const [clientFood, setClientFood] = useState(null); // daily calorie and protein totals (coach_food_summary), or null
  const foodRequestRef = useRef(null); // the client the latest food request was for
  const foodConfirmedTextRef = useRef(null); // a note the coach was warned about and chose to send as it is
  const withdrawnFoodSentenceRef = useRef(''); // the food sentence a note still held when the client stopped sharing it
  const [clientFlagCounts, setClientFlagCounts] = useState({}); // user_id -> unresolved count for card badges
  const [clientUnreadMessageCounts, setClientUnreadMessageCounts] = useState({}); // auth_user_id -> unread client-sent messages
  const [clientMessages, setClientMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [resolvingFlag, setResolvingFlag] = useState(null); // flag id being resolved
  const [resolveNote, setResolveNote] = useState('');
  const [sessionNoteEditing, setSessionNoteEditing] = useState(null); // session id being noted
  const [sessionNoteText, setSessionNoteText] = useState('');
  const [sessionNoteSaving, setSessionNoteSaving] = useState(false);
  const [flagSubSearch, setFlagSubSearch] = useState('');
  const [flagSubSelected, setFlagSubSelected] = useState(null); // { id, name }
  const [flagSubDropdownOpen, setFlagSubDropdownOpen] = useState(false);
  const [showDeleteSession, setShowDeleteSession] = useState(false);
  const [clientSessions, setClientSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [deletingSession, setDeletingSession] = useState(null);
  const [movingSession, setMovingSession] = useState(null);
  const [programDays, setProgramDays] = useState([]);
  const [moveTarget, setMoveTarget] = useState({});
  const [coachAuthId, setCoachAuthId] = useState(null);
  const [clientTotalCount, setClientTotalCount] = useState(null);
  const [clientPageLimit, setClientPageLimit] = useState(CLIENT_PAGE_SIZE);
  const [loadingMoreClients, setLoadingMoreClients] = useState(false);

  useEffect(() => {
    loadData();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setCoachAuthId(user.id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load check-in data when client is selected and checkins or nutrition tab is active
  useEffect(() => {
    if (selectedClient && (activeTab === 'checkins' || activeTab === 'nutrition')) {
      loadClientCheckIns();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, activeTab]);

  // Keep failed or in-flight reads separate from a confirmed empty response.
  useEffect(() => {
    let cancelled = false;
    setClientGlp1(null);
    setClientNutritionData(null);
    setNutritionStatus('loading');
    if (!selectedClient?.user_id) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Session unavailable');
        const res = await fetch('/api/nutrition-admin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ action: 'get_nutrition_profile', auth_user_id: selectedClient.user_id }),
        });
        if (!res.ok) throw new Error('Nutrition unavailable');
        const data = await res.json();
        if (!data || !Object.prototype.hasOwnProperty.call(data, 'profile')) throw new Error('Invalid response');
        if (cancelled) return;
        if (data.glp1) setClientGlp1(data.glp1);
        setClientNutritionData(data);
        setNutritionStatus('loaded');
      } catch {
        if (!cancelled) setNutritionStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClient?.user_id, nutritionRetry]);

  // Re-load analytics when timeRange changes
  useEffect(() => {
    if (selectedClient) {
      loadPerformanceAnalytics(selectedClient.user_id, timeRange);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  // The shareable brief: training, weight, food and swaps in one note. Rebuilt whenever fresh analytics
  // load (new client or time range). If only the swaps or the food totals arrive afterwards it is rebuilt
  // too, but never over text the coach has already changed.
  const lastBriefingRef = useRef('');
  const buildBriefing = () => generateCoachBrief(performanceAnalytics, {
    timeRangeWeeks: getEffectiveBriefingWeeks(selectedClient?.created_at, timeRange),
    food: clientFood,
    swaps: Array.isArray(clientSwaps) ? structureSwaps(clientSwaps).actOn : [],
  });
  useEffect(() => {
    if (performanceAnalytics) {
      lastBriefingRef.current = buildBriefing();
      setBriefingText(lastBriefingRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceAnalytics]);
  useEffect(() => {
    if (performanceAnalytics && !loadingAnalytics && briefingText === lastBriefingRef.current) {
      lastBriefingRef.current = buildBriefing();
      setBriefingText(lastBriefingRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSwaps, clientFood]);


  const loadClientCheckIns = async () => {
    if (!selectedClient) return;

    try {
      const userId = selectedClient.user_id;

      // Fetch check-in data
      const [weightData, checkInData] = await Promise.all([
        getWeightHistory(userId, 12), // Last 12 months
        getUserCheckIns(userId, { staff: true }),
      ]);


      setClientWeightHistory(weightData || []);
      setClientCheckIns(checkInData || []);

      // Starting weight = first weight_logs entry (single source of truth)
      // Fall back to users.weight (onboarding field) only if no logs exist
      const firstLogWeight = weightData?.[0]?.weight ?? null;
      if (firstLogWeight !== null) {
        setClientStartingWeight(firstLogWeight);
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('weight')
          .eq('auth_user_id', userId)
          .single();
        setClientStartingWeight(userData?.weight);
      }

      const { data: onboardingPhotoData } = await supabase
        .from('check_in_photos')
        .select('*')
        .eq('user_id', userId)
        .ilike('notes', '%Onboarding%')
        .order('created_at', { ascending: true });

      // Links from the server, which end with the coaching (migration 096)
      const signedUrls = await getStaffPhotoUrls(userId, (onboardingPhotoData || []).map((p) => p.photo_url));
      const resolvedOnboarding = (onboardingPhotoData || []).map((photo, i) => ({
        ...photo,
        photo_url: signedUrls[i],
      }));
      setClientOnboardingPhotos(resolvedOnboarding);
    } catch (error) {
      toastError('Failed to load check-in data');
    }
  };

  const loadData = async (limit = CLIENT_PAGE_SIZE, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMoreClients(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Get current user info to apply hierarchical filtering
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('id, auth_user_id, role, coach_id, admin_id')
        .eq('auth_user_id', authUser.id)
        .single();

      if (currentUserError) throw currentUserError;

      const currentRole = currentUserData.role;
      const currentUserId = currentUserData.auth_user_id;

      // Get clients from users table with hierarchical filtering.
      // Paginated via .range() — count: 'exact' also returns the total so the UI knows
      // whether a "Load More" is needed, without a second round-trip.
      let query = supabase
        .from('users')
        .select('*', { count: 'exact' })
        .eq('role', 'client')  // ✅ Only fetch clients, not coaches/admins
        .order('created_at', { ascending: false })
        .range(0, limit - 1);

      // Apply role-based filtering
      if (currentRole === 'coach') {
        // Coaches only see their own clients
        query = query.eq('coach_id', currentUserId);
      } else if (currentRole === 'admin') {
        // Admins see clients of their coaches
        // First get the admin's coaches
        const { data: adminCoaches, error: coachesError } = await supabase
          .from('users')
          .select('id, auth_user_id')
          .eq('admin_id', currentUserId)
          .eq('role', 'coach');

        if (coachesError) throw coachesError;

        const coachIds = (adminCoaches || []).map(c => c.auth_user_id);
        // Include clients of these coaches
        if (coachIds.length > 0) {
          query = query.in('coach_id', coachIds);
        } else {
          // Admin has no coaches yet, show empty list
          query = query.eq('id', -1); // Impossible condition to return empty
        }
      }
      // Super admin sees all clients (no additional filter)

      const { data: usersData, error: usersError, count: totalClients } = await query;

      if (usersError) throw usersError;
      setClientTotalCount(totalClients ?? null);
      setClientPageLimit(limit);

      // Map users with all relevant fields
      const allClients = (usersData || [])
        .filter(user => user.auth_user_id)
        .map(user => ({
          id: user.id,
          user_id: user.auth_user_id,
          full_name: user.name || user.full_name || 'Unknown',
          email: user.email,
          phone_number: user.phone_number,
          age: user.age,
          sex: user.sex,
          weight: user.weight,
          height: user.height,
          primary_goal: user.primary_goal,
          injuries: user.injuries,
          has_injuries: user.has_injuries,
          training_location: user.training_location,
          home_equipment: user.home_equipment,
          exercise_frequency: user.exercise_frequency,
          experience_level: user.experience_level,
          coach_code: user.coach_code,
          coach_id: user.coach_id,  // ✅ Include coach_id for reference
          created_at: user.created_at,
          needs_review: user.needs_review ?? false,
          // Lifecycle fields — drive the Active / Inactive split (see utils/lifecycle.js)
          status: user.status,
          subscription_status: user.subscription_status ?? null,
          is_complimentary: user.is_complimentary ?? false,
          archived_at: user.archived_at ?? null,
          archive_reason: user.archive_reason ?? null,
        }));

      // Get all programs
      const { data: programsData, error: programsError } = await supabase
        .from('programs')
        .select('*')
        .order('created_at', { ascending: false });

      if (programsError) throw programsError;

      // Who built the programs that are neither templates nor the signed-in user's own, so the
      // assign list can say so. Only a super_admin can read such a program in the first place.
      const builderIds = [...new Set((programsData || [])
        .filter(p => !p.is_client_copy && !p.is_template && p.created_by && p.created_by !== currentUserId)
        .map(p => p.created_by))];
      const builderNames = {};
      if (builderIds.length > 0) {
        const { data: builders, error: buildersError } = await supabase
          .from('users')
          .select('auth_user_id, name')
          .in('auth_user_id', builderIds);
        // Without the names the list still groups correctly; it just cannot say who.
        if (buildersError) console.error('Could not load program builders:', buildersError);
        (builders || []).forEach(b => { builderNames[b.auth_user_id] = b.name; });
      }

      // Get exercise library
      const { data: exercisesData } = await supabase
        .from('exercises')
        .select('*')
        .order('name', { ascending: true });

      // Get program assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('program_assignments')
        .select('*')
        .eq('is_active', true);

      if (assignmentsError) {
        console.error('Could not load program assignments:', assignmentsError);
        toastError('Could not load program assignments. Please try again.');
      }

      // Fetch workout sessions for last 30 days (for dashboard metrics)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: sessionsData, error: sessionsError } = await supabase
        .from('workout_sessions')
        .select('id, user_id, started_at, completed_at, program_day_id, coach_notes')
        .gte('started_at', thirtyDaysAgo.toISOString())
        .order('started_at', { ascending: false });

      if (sessionsError) {
        console.error('Could not load workout sessions:', sessionsError);
        toastError('Could not load workout sessions. Please try again.');
      }

      // Fetch coach information for displaying on client cards
      const uniqueCoachIds = [...new Set(allClients.map(c => c.coach_id).filter(Boolean))];
      const coachesMap = {};

      if (uniqueCoachIds.length > 0) {
        const { data: coachesData } = await supabase
          .from('users')
          .select('auth_user_id, name')
          .in('auth_user_id', uniqueCoachIds)
          .eq('role', 'coach');

        (coachesData || []).forEach(coach => {
          coachesMap[coach.auth_user_id] = coach.name;
        });
      }

      // Get active session IDs (sessions with completed workout logs)
      const sessionIds = (sessionsData || []).map(s => s.id);
      let activeSessionIdSet = new Set();
      if (sessionIds.length > 0) {
        const { data: completedLogs } = await supabase
          .from('workout_logs')
          .select('session_id')
          .in('session_id', sessionIds)
          .eq('completed', true);
        activeSessionIdSet = new Set(completedLogs?.map(l => l.session_id) || []);
      }

      // Compute metrics per client
      const metrics = computeClientMetrics(allClients, sessionsData || [], activeSessionIdSet, assignmentsData || []);

      // Fetch unresolved flag counts for all clients in one query
      const clientUserIds = allClients.map(c => c.user_id);
      const flagCountsMap = {};
      if (clientUserIds.length > 0) {
        const { data: flagsData } = await supabase
          .from('exercise_flags')
          .select('user_id')
          .in('user_id', clientUserIds)
          .is('resolved_at', null);
        (flagsData || []).forEach(flag => {
          flagCountsMap[flag.user_id] = (flagCountsMap[flag.user_id] || 0) + 1;
        });
      }
      setClientFlagCounts(flagCountsMap);

      // Fetch unread (client-sent, unread by coach) message counts for all clients in one query
      try {
        const unreadMap = await getUnreadCountsForClients(clientUserIds);
        setClientUnreadMessageCounts(unreadMap);
      } catch (e) {
        console.error('Failed to load unread message counts:', e);
      }

      setClients(allClients);
      if (initialClientId) {
        const match = allClients.find(c => c.id === initialClientId);
        if (match) selectClient(match);
      }
      setCoaches(coachesMap);  // ✅ Store coaches by ID
      setPrograms(programsData || []);
      setSignedInCoach({ authId: currentUserId, role: currentRole });
      setProgramBuilders(builderNames);
      setProgramAssignments(assignmentsData || []);
      setExerciseLibrary(exercisesData || []);
      setClientMetrics(metrics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMoreClients(false);
    }
  };

  const loadMoreClients = () => loadData(clientPageLimit + CLIENT_PAGE_SIZE, true);
  const hasMoreClients = clientTotalCount != null && clients.length < clientTotalCount;

  // Active / Inactive split for the client list. Inactive = archived, suspended,
  // canceled, or never-activated (see utils/lifecycle.js). Coaches only ever see
  // their own clients, so a simple two-way split is enough here.
  const isInactiveClient = (client) => getLifecycleBucket(client) === 'inactive';
  const visibleClients = clients.filter(c => (clientTab === 'inactive' ? isInactiveClient(c) : !isInactiveClient(c)));
  const inactiveClientCount = clients.filter(isInactiveClient).length;

  const handleArchiveClient = async (client, archived) => {
    setArchivingClient(client.user_id);
    try {
      const { data, error } = await supabase.rpc('set_client_archived', {
        target_auth_user_id: client.user_id,
        archived,
        reason: archived ? 'manual' : null,
      });
      if (error) throw error;
      setClients(prev => prev.map(c => c.user_id === client.user_id
        ? { ...c, archived_at: archived ? (data ?? new Date().toISOString()) : null, archive_reason: archived ? 'manual' : null }
        : c));
      toastSuccess(archived ? `${client.full_name} moved to Inactive` : `${client.full_name} restored`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setArchivingClient(null);
    }
  };

  // `week`: the week to show (the client's current week when not given, never an earlier one)
  const loadProgramDetails = async (programId, { useCache = true, week = null } = {}) => {
    const ticket = ++programLoadRef.current;
    setProgramLoadFailed(false);
    // Nothing of what was on screen stays editable while the week is read again: the rows are drawn anew
    // from what comes back (an input keeps what was typed in it otherwise; Codex's review 024, R4)
    setProgramDetails(null);
    setEditorWeek({ week: null, note: null });
    void useCache; // always fresh: the client's current week can move on between two openings
    // supabase-js resolves with { error } instead of throwing: a failed read must not pass as an
    // empty program, be shown as editable, or be kept in the cache (Codex's review 023, R3)
    const must = ({ data, error }) => { if (error) throw error; return data; };
    try {
      // The client's current week, read now rather than from the list loaded with the page, and only that
      // week: edits go to it and the weeks after it, never to past weeks (Codex's review 023, R1 and R6)
      const assignmentRows = must(await supabase
        .from('program_assignments')
        .select('current_week')
        .eq('program_id', programId)
        .eq('is_active', true));
      const currentWeek = assignmentRows.length === 1 ? (assignmentRows[0].current_week ?? 1) : null;
      if (currentWeek == null) {
        if (ticket !== programLoadRef.current) return;
        setEditorWeek({ week: null, note: 'This program has no single active client, so it cannot be edited here.' });
        setProgramDetails([]);
        return;
      }
      // The weeks that may be edited: the client's current one and any later week already made
      const madeWeeks = must(await supabase
        .from('program_weeks')
        .select('week_number')
        .eq('program_id', programId)
        .gte('week_number', currentWeek));
      // The older format anywhere in the programme is still edited by day number (057): such a programme
      // stays in the client's current week and gets no Add a day. Not knowing counts as yes (Codex's
      // reviews 032 R2 and 033 F1)
      const format = await supabase.rpc('editor_program_has_older_format', { p_program_id: programId });
      const legacy = format.error ? true : format.data !== false;
      const made = [...new Set((madeWeeks || []).map(w => w.week_number).filter(n => n >= currentWeek))].sort((a, b) => a - b);
      const latest = made.length ? made[made.length - 1] : currentWeek - 1;
      const choices = legacy ? made.filter(n => n === currentWeek) : made;
      const shown = week != null && choices.includes(week) ? week : currentWeek;
      const weeksData = must(await supabase
        .from('program_weeks')
        .select('*')
        .eq('program_id', programId)
        .eq('week_number', shown));
      if (ticket !== programLoadRef.current) return;
      const note = shown === currentWeek
        ? `Week ${shown}, the client's current week. Changes apply to this week and the weeks after it; past weeks stay as they were.`
        : `Week ${shown}, ahead of the client (on week ${currentWeek}). Changes apply to this week and the weeks after it.`;
      setEditorWeek(weeksData.length
        ? { week: shown, current: currentWeek, latest, choices, note, legacy }
        : { week: null, current: currentWeek, latest, choices, legacy, note: `Week ${currentWeek} has not been made yet. It is made when the client opens it, or with Generate next week.` });

      const weekIds = (weeksData || []).map(w => w.id);
      if (weekIds.length === 0) {
        if (ticket !== programLoadRef.current) return;
        setProgramDetails([]); return;
      }

      // Fetch everything for the whole program in a handful of batched queries
      // instead of firing separate blocks/items queries per day — with many days in
      // parallel, identical-shaped concurrent queries were colliding on Supabase's
      // pooled connections and coming back as 500s (some days would silently fail to load).
      const daysData = must(await supabase
        .from('program_days')
        .select('*')
        .in('week_id', weekIds)
        .order('day_number', { ascending: true }));

      const dayIds = (daysData || []).map(d => d.id);

      const blocksData = dayIds.length
        ? must(await supabase.from('exercise_blocks').select('*').in('day_id', dayIds).order('block_order', { ascending: true }))
        : [];

      const blockIds = (blocksData || []).map(b => b.id);

      const blockItemsData = blockIds.length
        ? must(await supabase.from('block_items').select('*').in('block_id', blockIds).order('position', { ascending: true }))
        : [];

      // Days with no blocks fall back to legacy program_exercises
      const daysWithBlocks = new Set((blocksData || []).map(b => b.day_id));
      const legacyDayIds = dayIds.filter(id => !daysWithBlocks.has(id));

      const legacyExercisesData = legacyDayIds.length
        ? must(await supabase.from('program_exercises').select('*').in('day_id', legacyDayIds).order('exercise_order', { ascending: true }))
        : [];

      const exerciseIds = [...new Set([
        ...(blockItemsData || []).map(bi => bi.exercise_id),
        ...(legacyExercisesData || []).map(pe => pe.exercise_id),
      ].filter(Boolean))];

      let exercisesMap = {};
      if (exerciseIds.length > 0) {
        const exercisesData = must(await supabase
          .from('exercises')
          .select('*')
          .in('id', exerciseIds));
        (exercisesData || []).forEach(ex => { exercisesMap[ex.id] = ex; });
      }

      // Reassemble the same nested shape the UI expects
      const blockItemsByBlock = {};
      (blockItemsData || []).forEach(bi => {
        if (!blockItemsByBlock[bi.block_id]) blockItemsByBlock[bi.block_id] = [];
        blockItemsByBlock[bi.block_id].push({ ...bi, exercise: exercisesMap[bi.exercise_id] || null });
      });

      const blocksByDay = {};
      (blocksData || []).forEach(b => {
        if (!blocksByDay[b.day_id]) blocksByDay[b.day_id] = [];
        blocksByDay[b.day_id].push({ ...b, items: blockItemsByBlock[b.id] || [] });
      });

      const legacyByDay = {};
      (legacyExercisesData || []).forEach(pe => {
        if (!legacyByDay[pe.day_id]) legacyByDay[pe.day_id] = [];
        legacyByDay[pe.day_id].push({ ...pe, exercise: exercisesMap[pe.exercise_id] || null });
      });

      const daysByWeek = {};
      (daysData || []).forEach(d => {
        if (!daysByWeek[d.week_id]) daysByWeek[d.week_id] = [];
        daysByWeek[d.week_id].push({
          ...d,
          blocks: blocksByDay[d.id] || [],
          exercises: legacyByDay[d.id] || [],
        });
      });

      const weeksWithDays = (weeksData || []).map(w => ({
        ...w,
        days: (daysByWeek[w.id] || []).sort((a, b) => a.day_number - b.day_number),
      }));

      if (ticket !== programLoadRef.current) return; // another program was opened meanwhile
      setProgramDetails(weeksWithDays);
    } catch (err) {
      console.error('Could not load program details:', err);
      if (ticket !== programLoadRef.current) return;
      setProgramLoadFailed(true);
      toastError('Could not load program details. Please try again.');
    }
  };

  const assignProgram = async (userId, programId) => {
    if (!programId || !userId) {
      toastError('Please select a program first.');
      return;
    }
    if (assigningTo) return;
    setAssigningTo(userId);
    try {
      // Clones first, then swaps the assignment, restoring the old one if the swap fails.
      await assignProgramToClient({ userId, programId });

      await loadData();
      setSelectedProgram('');
      setShowAssignPanel(false);
      if (selectedClient) loadProgramHistory(selectedClient.user_id);
      toastSuccess('Program assigned successfully!');
    } catch (err) {
      toastError('Error assigning program: ' + (err.message || 'Unknown error'));
      // A failed swap can still have changed what the client has, so show what is really there.
      loadData();
    } finally {
      setAssigningTo(null);
    }
  };

  const markAsReviewed = async (userId) => {
    await supabase.from('users').update({ needs_review: false }).eq('auth_user_id', userId);
    setClients(prev => prev.map(c => c.user_id === userId ? { ...c, needs_review: false } : c));
    if (selectedClient?.user_id === userId) {
      setSelectedClient(prev => ({ ...prev, needs_review: false }));
    }
  };

  const openAdvanceWeek = async (programId, currentWeek) => {
    setShowAdvanceWeek(p => !p);
    setAdvanceWeekTarget('');
    setShowAssignPanel(false);
    if (!showAdvanceWeek) {
      // Fetch actual weeks that exist for this program
      const { data } = await supabase
        .from('program_weeks')
        .select('week_number')
        .eq('program_id', programId)
        .order('week_number');
      setAvailableWeeks(data?.map(w => w.week_number) || [currentWeek]);
    }
  };

  const handleAdvanceWeek = async (userId, programId, targetWeek) => {
    setAdvancingWeek(true);
    try {
      // The week and its clock together, so the client's screen keeps them on it (migration 092)
      await setClientWeek(userId, targetWeek);

      // When moving backward, null out completed_at on sessions for the target week's days
      // so determineAndSetCurrentWeek() doesn't auto-advance past it again.
      const { data: targetWeekData } = await supabase
        .from('program_weeks')
        .select('id, program_days(id)')
        .eq('program_id', programId)
        .eq('week_number', targetWeek)
        .maybeSingle();
      const targetDayIds = targetWeekData?.program_days?.map(d => d.id) || [];
      if (targetDayIds.length > 0) {
        await supabase
          .from('workout_sessions')
          .update({ completed_at: null })
          .eq('user_id', userId)
          .in('day_id', targetDayIds);
      }

      toastSuccess(`Client moved to Week ${targetWeek}.`);
      setShowAdvanceWeek(false);
      setAdvanceWeekTarget('');
      setAvailableWeeks([]);
      delete programDetailsCacheRef.current[programId];
      await loadProgramHistory(userId);
    } catch (err) {
      console.error('Advance week failed:', err);
      toastError('Failed to advance week. Please try again.');
    } finally {
      setAdvancingWeek(false);
    }
  };

  const handleGenerateNextWeek = async (programId, userId) => {
    setGeneratingNextWeek(true);
    try {
      // Find how many weeks currently exist for this program
      const { data: existingWeeks } = await supabase
        .from('program_weeks')
        .select('week_number')
        .eq('program_id', programId)
        .order('week_number', { ascending: false })
        .limit(1);
      const lastWeekNumber = existingWeeks?.[0]?.week_number || 1;
      await generateNextWeek(programId, lastWeekNumber);
      // Advance current_week to the newly generated week, with its clock (migration 092)
      await setClientWeek(userId, lastWeekNumber + 1);
      toastSuccess(`Week ${lastWeekNumber + 1} generated and client moved forward.`);
      setShowAdvanceWeek(false);
      setAvailableWeeks([]);
      delete programDetailsCacheRef.current[programId];
      await logCoachingDecision(
        'week_advance',
        { week_number: lastWeekNumber },
        { week_number: lastWeekNumber + 1 },
        { program_id: programId }
      );
      await loadProgramHistory(userId);
    } catch (err) {
      console.error('Week generation failed:', err);
      toastError('Failed to generate next week. Please try again.');
    } finally {
      setGeneratingNextWeek(false);
    }
  };

  const loadProgramDays = async (programId) => {
    const { data: weeks } = await supabase
      .from('program_weeks')
      .select('id, week_number')
      .eq('program_id', programId)
      .order('week_number');
    const weekIds = (weeks || []).map(w => w.id);
    if (weekIds.length === 0) { setProgramDays([]); return; }
    const weekMap = Object.fromEntries((weeks || []).map(w => [w.id, w.week_number]));
    const { data: days } = await supabase
      .from('program_days')
      .select('id, day_number, week_id')
      .in('week_id', weekIds)
      .order('week_id')
      .order('day_number');
    setProgramDays((days || []).map(d => ({ ...d, program_weeks: { week_number: weekMap[d.week_id] } })));
  };

  const handleMoveSession = async (sessionId, newDayId, userId, programId) => {
    setMovingSession(sessionId);
    try {
      const { error } = await supabase
        .from('workout_sessions')
        .update({ day_id: newDayId })
        .eq('id', sessionId);
      if (error) throw error;
      const matched = programDays.find(d => d.id === newDayId || d.id === parseInt(newDayId));
      setClientSessions(prev => prev.map(s => s.id === sessionId
        ? { ...s, day_id: newDayId, program_days: { day_number: matched?.day_number, program_weeks: matched?.program_weeks } }
        : s
      ));
      setMoveTarget({});
      toastSuccess('Session moved — client will now land on the correct week/day.');
    } catch (err) {
      console.error('Failed to move session:', err);
      toastError('Failed to move session.');
    } finally {
      setMovingSession(null);
    }
  };

  const loadClientSessions = async (userId, programId) => {
    setLoadingSessions(true);
    try {
      const { data: raw, error } = await supabase
        .from('workout_sessions')
        .select('id, started_at, completed_at, day_id, program_days(day_number, program_weeks(week_number))')
        .eq('user_id', userId)
        .eq('program_id', programId)
        .order('started_at', { ascending: false })
        .limit(50);
      if (error) throw error;

      const all = raw || [];
      if (all.length === 0) { setClientSessions([]); return; }

      // Only show sessions that have at least one completed workout log
      const { data: completedLogs } = await supabase
        .from('workout_logs')
        .select('session_id')
        .in('session_id', all.map(s => s.id))
        .eq('completed', true);
      const activeIds = new Set(completedLogs?.map(l => l.session_id) || []);
      setClientSessions(all.filter(s => activeIds.has(s.id) || s.completed_at));
    } catch (err) {
      console.error('Failed to load sessions:', err);
      toastError('Failed to load sessions.');
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleDeleteSession = async (sessionId, userId, programId) => {
    setDeletingSession(sessionId);
    try {
      // Best-effort: delete logs first (may be blocked by RLS if admin deleting client data)
      await supabase.from('workout_logs').delete().eq('session_id', sessionId);

      const { error: sessionError } = await supabase
        .from('workout_sessions')
        .delete()
        .eq('id', sessionId);
      if (sessionError) throw sessionError;

      setClientSessions(prev => prev.filter(s => s.id !== sessionId));
      toastSuccess('Session deleted.');
    } catch (err) {
      console.error('Failed to delete session:', err);
      toastError('Failed to delete session.');
    } finally {
      setDeletingSession(null);
    }
  };

  const handleConfirmUnassign = async () => {
    const { programId, userId } = confirmDialog;
    setUnassignLoading(true);

    try {
      // Soft-delete: mark inactive so program history is preserved
      const { data, error } = await supabase
        .from('program_assignments')
        .update({ is_active: false, ended_at: new Date().toISOString() })
        .eq('program_id', programId)
        .eq('user_id', userId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        toastError('Assignment not found');
        return;
      }
      // Archive the client copy so it's hidden from active views and the program library.
      // Since migration 070 the database does this itself when the assignment ends, for whoever ends
      // it. This request is the older way and is kept for a database that does not have 070 yet: it
      // only ever worked for a super_admin or the coach who built the copy, and for anyone else it
      // updates no row and reports no error, which is why its result is not read.
      await supabase
        .from('programs')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', programId)
        .eq('is_client_copy', true);

      // Update local state immediately for instant UI feedback
      setProgramAssignments(prev =>
        prev.filter(pa => !(pa.program_id === programId && pa.user_id === userId))
      );

      // Clear editing state if the unassigned program was being edited
      if (editingProgram?.id === programId) {
        setEditingProgram(null);
        setProgramDetails(null);
      }

      toastSuccess('Program unassigned');
      if (selectedClient) loadProgramHistory(selectedClient.user_id);

      // Close dialog
      setConfirmDialog({ isOpen: false, programId: null, userId: null });
    } catch (err) {
      toastError('Error: ' + (err.message || 'Unknown error'));
    } finally {
      setUnassignLoading(false);
    }
  };

  const deleteClient = async () => {
    const client = deleteDialog.client;
    if (!client) return;

    setDeleteLoading(true);
    try {
      // Delete client copy programs before removing assignments
      const { data: clientAssignments } = await supabase
        .from('program_assignments')
        .select('program_id')
        .eq('user_id', client.user_id);

      const { data: clientCopies } = clientAssignments?.length
        ? await supabase
            .from('programs')
            .select('id')
            .in('id', clientAssignments.map(a => a.program_id))
            .eq('is_client_copy', true)
        : { data: [] };

      for (const copy of (clientCopies || [])) {
        await deleteClientCopyProgram(copy.id);
      }

      // Delete from program_assignments (foreign key constraint)
      const { error: assignError } = await supabase
        .from('program_assignments')
        .delete()
        .eq('user_id', client.user_id);
      if (assignError) throw new Error('Failed to delete program assignments: ' + assignError.message);

      // Delete workout sessions
      const { error: sessError } = await supabase
        .from('workout_sessions')
        .delete()
        .eq('user_id', client.user_id);
      if (sessError) throw new Error('Failed to delete workout sessions: ' + sessError.message);

      // Delete check-in photos
      const { error: photosError } = await supabase
        .from('check_in_photos')
        .delete()
        .eq('user_id', client.user_id);
      if (photosError) throw new Error('Failed to delete check-in photos: ' + photosError.message);

      // Delete from users table
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('id', client.id);
      if (userError) throw new Error('Failed to delete user record: ' + userError.message);

      toastSuccess('Client deleted successfully');
      setDeleteDialog({ isOpen: false, client: null });
      setSelectedClient(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting client:', err);
      toastError('Error deleting client: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Legacy (non-block) equivalent of updateBlockItem — same local-patch
  // approach instead of a full program refetch on every field blur, and the
  // same forward cascade swapExercise already does (see below): the editor
  // only shows Week 1 as the canonical template, so without cascading, a
  // sets/reps/rest edit here would only ever change a week the client has
  // already moved past — invisible in their actual current week.
  const updateExercise = async (peId, field, value, exerciseId = null, dayNumber = null) => {
    try {
      if (exerciseId != null && dayNumber != null) {
        // One atomic request: this row plus the same exercise on the same day in the client's
        // current and later weeks (migration 057) — it lands everywhere or nowhere.
        const { programId, currentWeek } = getCascadeContext();
        const res = await updateProgramExerciseCascade({
          peId, matchExerciseId: exerciseId, patch: { [field]: value }, programId, currentWeek, dayNumber,
        });
        if (!res.ok) throw res.error;
      } else {
        const { error } = await supabase
          .from('program_exercises')
          .update({ [field]: value })
          .eq('id', peId);
        if (error) throw error;
      }

      setProgramDetails(prev => {
        if (!prev) return prev;
        return prev.map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            exercises: (day.exercises || []).map(pe =>
              pe.id === peId ? { ...pe, [field]: value } : pe
            ),
          })),
        }));
      });
      if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      return true;
    } catch (err) {
      console.error('Could not update exercise:', err);
      toastError('Could not update exercise. Please try again.');
      return false;
    }
  };

  // An editor call the database refused because the screen is out of date: say so, and show it as it is now
  const refusedAsOutOfDate = (err) => {
    const why = editorRefusal(err);
    if (!why) return false;
    // `editingProgram` is the program this save began on; if the coach has moved on, leave the new one be
    if (!editingProgram || editingProgramRef.current !== editingProgram.id) return true;
    toastError(why);
    loadProgramDetails(editingProgram.id, { useCache: false });
    return true;
  };

  // What a forward cascade needs (the program and the week the client is on), read from
  // already-loaded local state — no extra round trip. Snapshotted when the edit is made, so
  // anything that runs later (the add-exercise Retry) still targets the same program and week.
  const getCascadeContext = () => {
    const assignment = programAssignments.find(
      pa => pa.program_id === editingProgram.id && pa.user_id === selectedClient?.user_id
    );
    return { programId: editingProgram.id, currentWeek: assignment?.current_week ?? 1 };
  };

  // Update sets/reps on a single block_item row (rest is updateBlockRest, below). Patches local state
  // directly instead of re-fetching the whole program (same pattern as the
  // swaps below) — a full nested-join refetch on every
  // single field blur was the main source of this screen feeling slow.
  // Also cascades to the same block position in future weeks, same as
  // swapBlockExercise/addExerciseToDay — the editor only shows Week 1 as
  // the canonical template, so without this a sets/reps/rest edit made here
  // never reached whatever week the client is actually on.
  const updateBlockItem = async (itemId, field, value, exerciseId = null, blockOrder = null, dayNumber = null) => {
    try {
      if (exerciseId != null && blockOrder != null && dayNumber != null) {
        // One atomic request: this row in the client's current week and the same row in later weeks; the
        // database works out where it sits (migration 085)
        const res = await editorUpdateBlockItem({ itemId, seenExerciseId: exerciseId, patch: { [field]: value } });
        if (!res.ok) throw res.error;
      } else {
        const { error } = await supabase
          .from('block_items')
          .update({ [field]: value })
          .eq('id', itemId);
        if (error) throw error;
      }

      setProgramDetails(prev => {
        if (!prev) return prev;
        return prev.map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            blocks: day.blocks.map(block => ({
              ...block,
              items: block.items.map(item =>
                item.id === itemId ? { ...item, [field]: value } : item
              ),
            })),
          })),
        }));
      });
      if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      return true;
    } catch (err) {
      if (refusedAsOutOfDate(err)) return false;
      console.error('Could not update block item:', err);
      toastError('Failed to save change');
      return false;
    }
  };

  // Set the rest time of a block. Rest is a property of the BLOCK (exercise_blocks.rest_seconds), which
  // is what the client app reads, so it does not go through updateBlockItem: block_items has no such
  // column, and that is why this box could never save. Cascades to the same block position in the
  // client's current and later weeks, in one atomic request (migration 062).
  const updateBlockRest = async (blockId, restSeconds, exerciseId, blockOrder, dayNumber) => {
    try {
      const res = await editorUpdateBlockRest({ blockId, seenExerciseId: exerciseId, restSeconds });
      if (!res.ok) throw res.error;

      const changed = new Set([blockId, ...res.blockIds]);
      setProgramDetails(prev => {
        if (!prev) return prev;
        return prev.map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            blocks: day.blocks.map(block =>
              changed.has(block.id) ? { ...block, rest_seconds: restSeconds } : block
            ),
          })),
        }));
      });
      if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      return true;
    } catch (err) {
      if (refusedAsOutOfDate(err)) return false;
      console.error('Could not update block rest:', err);
      toastError('Failed to save change');
      return false;
    }
  };

  // Swap exercise in a block-based program — updates by primary key, cascades forward
  const logCoachingDecision = async (decisionType, beforeValue, afterValue, extraContext = {}, coachNote = null) => {
    if (!coachAuthId || !selectedClient) return;
    // Note: the Supabase JS client resolves with { error } rather than throwing on a
    // DB-level failure (e.g. a type mismatch) — a try/catch here would never actually
    // see it. Checking `error` directly is what catches it, and previously nothing did:
    // coaching_decisions silently collected zero rows despite real coach activity.
    const { error } = await supabase.from('coaching_decisions').insert({
      coach_id: coachAuthId,
      client_user_id: selectedClient.user_id,
      program_id: editingProgram?.id ?? null,
      week_number: editingProgram ? getCascadeContext().currentWeek : null,
      decision_type: decisionType,
      before_value: beforeValue,
      after_value: afterValue,
      coach_note: coachNote || null,
      client_context: {
        completion_rate: performanceAnalytics?.completionRate ?? null,
        bodyweight_kg: selectedClient.bodyweight_kg ?? null,
        ...extraContext,
      },
    });
    if (error) {
      // Non-fatal — never block the coach action, but never fail silently either
      console.error('Failed to log coaching decision:', error);
    }
  };

  const swapBlockExercise = async (itemId, oldExerciseId, blockOrder, dayNumber, newExerciseId) => {
    if (swapSaving) return; // ignore re-entrant clicks while a swap is already in flight
    setSwapSaving(true);
    try {
      const oldEx = exerciseLibrary.find(e => e.id === oldExerciseId);
      const newEx = exerciseLibrary.find(e => e.id === newExerciseId);

      // One atomic request: the shown row plus the same exercise at the same block position in
      // the client's current and later weeks (migration 057) — it lands everywhere or nowhere,
      // so a failure here means nothing changed and the swap dialog stays open to try again.
      const res = await editorUpdateBlockItem({ itemId, seenExerciseId: oldExerciseId, patch: { exercise_id: newExerciseId } });
      if (!res.ok) throw res.error;

      // Remove the old exercise's avg so it doesn't bleed onto anything else
      setExerciseAvgs(prev => { const n = { ...prev }; delete n[oldExerciseId]; return n; });
      toastSuccess('Exercise swapped');
      const reason = swapReason;
      setSwappingExercise(null);
      setSwapSearch('');
      setPendingSwapEx(null);
      setSwapReason('');
      // Mirror the writes locally instead of a full loadProgramDetails refetch
      setProgramDetails(prev => patchBlockItemsTree(prev, {
        itemId,
        blockIds: res.blockIds,
        oldExerciseId,
        transform: (item) => ({ ...item, exercise_id: newExerciseId, exercise: newEx || null }),
      }));
      if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      await logCoachingDecision(
        'exercise_swap',
        { exercise_id: oldExerciseId, exercise_name: oldEx?.name ?? null, block_order: blockOrder },
        { exercise_id: newExerciseId, exercise_name: newEx?.name ?? null },
        { day_number: dayNumber, block_order: blockOrder },
        reason || null
      );
    } catch (err) {
      if (refusedAsOutOfDate(err)) { setSwappingExercise(null); return; }
      toastError('Failed to swap: ' + (err.message || 'Unknown error'));
    } finally {
      setSwapSaving(false);
    }
  };

  // Swap exercise in a legacy (non-block) program — updates by primary key, cascades forward
  const swapExercise = async (peId, oldExerciseId, dayNumber, newExerciseId) => {
    if (swapSaving) return; // ignore re-entrant clicks while a swap is already in flight
    setSwapSaving(true);
    try {
      const oldEx = exerciseLibrary.find(e => e.id === oldExerciseId);
      const newEx = exerciseLibrary.find(e => e.id === newExerciseId);

      // One atomic request: the shown row plus the same exercise on the same day in the client's
      // current and later weeks (migration 057) — it lands everywhere or nowhere.
      const { programId, currentWeek } = getCascadeContext();
      const res = await updateProgramExerciseCascade({
        peId, matchExerciseId: oldExerciseId, patch: { exercise_id: newExerciseId },
        programId, currentWeek, dayNumber,
      });
      if (!res.ok) throw res.error;

      // Remove the old exercise's avg so it doesn't bleed onto anything else
      setExerciseAvgs(prev => { const n = { ...prev }; delete n[oldExerciseId]; return n; });
      toastSuccess('Exercise swapped');
      const reason = swapReason;
      setSwappingExercise(null);
      setSwapSearch('');
      setPendingSwapEx(null);
      setSwapReason('');
      // Mirror the writes locally instead of a full loadProgramDetails refetch
      setProgramDetails(prev => patchLegacyExercisesTree(prev, {
        peId,
        dayIds: res.dayIds,
        oldExerciseId,
        transform: (pe) => ({ ...pe, exercise_id: newExerciseId, exercise: newEx || null }),
      }));
      if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      await logCoachingDecision(
        'exercise_swap',
        { exercise_id: oldExerciseId, exercise_name: oldEx?.name ?? null },
        { exercise_id: newExerciseId, exercise_name: newEx?.name ?? null },
        { day_number: dayNumber },
        reason || null
      );
    } catch (err) {
      toastError('Failed to swap: ' + (err.message || 'Unknown error'));
    } finally {
      setSwapSaving(false);
    }
  };

  // Delete a block exercise — removes by primary key, cascades forward
  const deleteBlockItem = (itemId, exerciseId, blockOrder, dayNumber) => {
    setRemoveExerciseDialog({
      isOpen: true,
      action: async () => {
        // One atomic request: the shown row plus the same exercise at the same block position in
        // the client's current and later weeks (migration 057) — removed everywhere or nowhere.
        const res = await editorDeleteBlockItem({ itemId, seenExerciseId: exerciseId });
        if (!res.ok) {
          if (!refusedAsOutOfDate(res.error)) toastError('Failed to delete: ' + (res.error?.message || 'Unknown error'));
          return;
        }
        toastSuccess('Exercise removed');
        // Mirror the deletes locally instead of a full loadProgramDetails refetch
        setProgramDetails(prev => patchBlockItemsTree(prev, {
          itemId,
          blockIds: res.blockIds,
          oldExerciseId: exerciseId,
          transform: () => null,
        }));
        if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      },
    });
  };

  // Delete a legacy exercise — removes by primary key, cascades forward
  const deleteProgramExercise = (peId, exerciseId, dayNumber) => {
    setRemoveExerciseDialog({
      isOpen: true,
      action: async () => {
        // One atomic request: the shown row plus the same exercise on the same day in the client's
        // current and later weeks (migration 057) — removed everywhere or nowhere.
        const { programId, currentWeek } = getCascadeContext();
        const res = await deleteProgramExerciseCascade({
          peId, matchExerciseId: exerciseId, programId, currentWeek, dayNumber,
        });
        if (!res.ok) { toastError('Failed to delete: ' + (res.error?.message || 'Unknown error')); return; }
        toastSuccess('Exercise removed');
        // Mirror the deletes locally instead of a full loadProgramDetails refetch
        setProgramDetails(prev => patchLegacyExercisesTree(prev, {
          peId,
          dayIds: res.dayIds,
          oldExerciseId: exerciseId,
          transform: () => null,
        }));
        if (editingProgram) delete programDetailsCacheRef.current[editingProgram.id];
      },
    });
  };

  // Add a brand new standalone block+item to a day, cascade to all forward generated weeks
  const addExerciseToDay = async (dayId, dayNumber, isBlock, exerciseId) => {
    if (addExerciseSaving) return; // ignore re-entrant clicks while an insert is already in flight
    setAddExerciseSaving(true);
    try {
      const exercise = exerciseLibrary.find(e => e.id === exerciseId);
      const { sets: defaultSets, reps: defaultReps } = getDefaultPrescription(exercise);

      if (isBlock) {
        // One request, one transaction: the day in the client's current week and the same day of every
        // later week, all or none (migration 085; Codex's review 023, R2). The request id is kept for
        // this add, so a retry returns the first answer instead of adding it twice.
        const key = `${editingProgram?.id}:${dayId}:${exerciseId}`;
        if (!unansweredAdds.has(key)) unansweredAdds.set(key, crypto.randomUUID());
        const res = await editorAddExercise({
          dayId, exerciseId, sets: defaultSets, reps: defaultReps, requestId: unansweredAdds.get(key),
        });
        if (!res.ok) {
          // Refused, or a plain error with a code: nothing was added, so the next try is a new addition.
          // No answer at all (no code): it may have landed, so the same choice again finishes it safely.
          if (res.error?.code || res.error?.hint) unansweredAdds.delete(key);
          else res.error.unanswered = true;
          throw res.error;
        }
        unansweredAdds.delete(key);
      } else {
        // Legacy program_exercises path
        const { data: existingPE, error: existingPEErr } = await supabase
          .from('program_exercises')
          .select('exercise_order')
          .eq('day_id', dayId)
          .order('exercise_order', { ascending: false })
          .limit(1);
        if (existingPEErr) throw existingPEErr;
        const newOrder = existingPE && existingPE.length > 0 ? existingPE[0].exercise_order + 1 : 1;

        const { error: peErr } = await supabase
          .from('program_exercises')
          .insert({ day_id: dayId, exercise_id: exerciseId, exercise_order: newOrder, sets: defaultSets, reps: defaultReps });
        if (peErr) throw peErr;
      }

      toastSuccess('Exercise added');
      setAddingExercise(null);
      setAddExerciseSearch('');
      // New server-generated rows: read the week again, if it is still the program on screen
      if (editingProgram && editingProgramRef.current === editingProgram.id) {
        loadProgramDetails(editingProgram.id, { useCache: false, week: editorWeek.week });
      }
    } catch (err) {
      if (refusedAsOutOfDate(err)) { setAddingExercise(null); return; }
      if (err?.unanswered) {
        toastError('No answer from the server, so it may or may not have been added. Choose the same exercise again to finish: it will not be added twice.');
        return;
      }
      toastError('Failed to add exercise: ' + (err.message || 'Unknown error'));
    } finally {
      setAddExerciseSaving(false);
    }
  };

  // Add a day (migration 087): from the week shown if it is after the client's, else from their next week.
  // The database is asked first; only if that week is not made yet is it made (without moving the client
  // on) and the same request sent again, so a database without 087 refuses before any week is made
  // (Codex's review 032). `place` is 'start', 'end' or 'after' the day `afterSlot`. Resolves true if added.
  const addDay = async ({ name, place, afterSlot = null, placeLabel = '' }) => {
    if (!editingProgram || addDaySaving || editorWeek.current == null) return false;
    const programId = editingProgram.id;
    const fromWeek = Math.max(editorWeek.week ?? 0, editorWeek.current + 1);
    const key = `day:${programId}:${fromWeek}:${place}:${afterSlot ?? ''}:${name}`;
    setAddDaySaving(true);
    try {
      if (!unansweredAdds.has(key)) unansweredAdds.set(key, crypto.randomUUID());
      const send = () => editorAddDay({ programId, fromWeek, place, afterSlot, name, requestId: unansweredAdds.get(key) });
      let res = await send();
      if (!res.ok && res.error?.hint === 'week_not_made' && fromWeek === editorWeek.latest + 1) {
        await generateNextWeek(programId, editorWeek.latest);
        res = await send();
      }
      if (!res.ok) {
        if (res.error?.code || res.error?.hint) unansweredAdds.delete(key);
        else {
          // No answer: it may have been added. The same name and place again reuses the request id, so it
          // cannot be added twice (Codex's review 032)
          toastError('No answer from the server, so the day may already have been added. Adding it again with the same name and place will not add it twice.');
          return false;
        }
        throw res.error;
      }
      unansweredAdds.delete(key);
      toastSuccess(`${name} added from week ${fromWeek}`);
      await logCoachingDecision('day_added', null, { name, place: placeLabel || place, from_week: fromWeek }, { program_id: programId });
      if (editingProgramRef.current === programId) loadProgramDetails(programId, { useCache: false, week: fromWeek });
      return true;
    } catch (err) {
      if (refusedAsOutOfDate(err)) return false;
      toastError('Could not add the day: ' + (err.message || 'Unknown error'));
      return false;
    } finally {
      setAddDaySaving(false);
    }
  };

  const getSuggestedExercises = (currentExercise) => {
    if (!currentExercise) return exerciseLibrary;

    const currentCategory = currentExercise.movement_category;
    const currentSecondary = currentExercise.secondary_muscles
      ? currentExercise.secondary_muscles.split(', ')
      : [];

    // Score exercises by relevance
    const scored = exerciseLibrary
      .filter(ex => ex.id !== currentExercise.id)
      .map(ex => {
        let score = 0;

        // Same movement category gets high score
        if (ex.movement_category === currentCategory) score += 10;

        // Secondary muscle overlap adds to score
        if (ex.secondary_muscles && currentSecondary.length > 0) {
          const exSecondary = ex.secondary_muscles.split(', ');
          const overlap = currentSecondary.filter(m => exSecondary.includes(m)).length;
          score += overlap * 5;
        }

        return { ...ex, score };
      });

    // Sort by score (descending), then by name
    return scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.name.localeCompare(b.name);
    });
  };


  // Get programs assigned to a user via program_assignments table
  const getClientPrograms = (userId) => {

    const assignedProgramIds = programAssignments
      .filter(pa => pa.user_id === userId)
      .map(pa => pa.program_id);


    const clientPrograms = programs.filter(p => assignedProgramIds.includes(p.id));

    return clientPrograms;
  };

  // Programs offered for assignment, in labelled groups: the curated template library, the
  // signed-in user's own programs, and (for a super_admin, who can read them) programs other
  // coaches built for themselves. Never client copies.
  const getAssignableProgramGroups = (excludeId = null) =>
    groupAssignablePrograms(programs, {
      myAuthId: signedInCoach.authId,
      role: signedInCoach.role,
      builderNames: programBuilders,
      excludeId,
    });

  // Load performance analytics for selected client
  const loadPerformanceAnalytics = async (userId, weeks = 12) => {
    setLoadingAnalytics(true);
    try {
      const isAllTime = weeks === 'all';
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (isAllTime ? 0 : weeks * 7));
      const dateLabel = date => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      setAnalyticsDateSpan(isAllTime ? 'All recorded dates' : dateLabel(startDate) + ' – ' + dateLabel(new Date()));

      // 'all' drops the date filter entirely, so cap with .limit(10000) instead —
      // same guard as the client-side stats queries (see historyService.js /
      // muscleGroupService.js), otherwise a heavy account silently hits Supabase's
      // default 1000-row cap.
      //
      // Order DESC so that when the cap bites on a heavy 'all time' account the
      // rows kept are the most RECENT ones (ascending + cap silently dropped the
      // latest weeks). Downstream consumers that assume chronological order
      // (per-exercise PR running-max, first-half/second-half trend split) get an
      // explicit ascending sort applied to `completedSets` right after fetch.
      let logsQuery = supabase
        .from('workout_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('completed', true);
      if (!isAllTime) logsQuery = logsQuery.gte('created_at', startDate.toISOString());
      logsQuery = logsQuery.order('created_at', { ascending: false }).limit(10000);

      let sessionsQuery = supabase
        .from('workout_sessions')
        .select('id, started_at, completed_at, rpe, energy_rating')
        .eq('user_id', userId);
      if (!isAllTime) sessionsQuery = sessionsQuery.gte('started_at', startDate.toISOString());
      sessionsQuery = sessionsQuery.limit(10000);

      let skippedQuery = supabase
        .from('workout_logs')
        .select('exercise_id')
        .eq('user_id', userId)
        .eq('skipped', true);
      if (!isAllTime) skippedQuery = skippedQuery.gte('created_at', startDate.toISOString());
      skippedQuery = skippedQuery.limit(10000);

      // Fetch workout_logs + bodyweight + sessions + client bodyweight in parallel
      const [logsResult, weightHistory, sessionsResult, clientBwResult, skippedResult, activeAssignmentResult, abandonedResult, strengthSummary] = await Promise.all([
        logsQuery,
        getWeightHistory(userId),
        sessionsQuery,
        supabase
          .from('users')
          .select('bodyweight_kg')
          .eq('auth_user_id', userId)
          .single(),
        skippedQuery,
        supabase
          .from('program_assignments')
          .select('program_id, current_week')
          .eq('user_id', userId)
          .eq('is_active', true)
          .maybeSingle(),
        // Abandoned sessions — started but never completed or marked missed, and old
        // enough (48h+) that it's not just someone paused overnight. Deliberately
        // unbounded by the timeRange filter above (a session abandoned months ago is
        // still worth surfacing) and read-only — nothing here touches the data, this
        // is purely so a coach can see it and decide what to do.
        //
        // workout_logs!inner requires at least ONE completed set. A session row is
        // created the moment a day is OPENED (WorkoutLogger._resolveSession), before
        // anything is logged, so without this a client who tapped into a workout and
        // backed out looked identical to one who quit halfway through. Across the
        // project that was 152 of 175 entries — the list was 87% noise, and one client
        // showed 27 "abandoned workouts" having never logged a set in any of them.
        supabase
          .from('workout_sessions')
          .select('id, started_at, workout_logs!inner(id)')
          .eq('user_id', userId)
          .eq('status', 'in_progress')
          .eq('workout_logs.completed', true)
          .lt('started_at', new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString())
          .order('started_at', { ascending: false }),
        // Same 1RM-based regression signal that powers StrengthProgress's "Needs
        // Attention" tier (StrengthProgress.jsx) — replaces the old stalledLifts
        // computation below, which compared raw per-set weight averages split
        // arbitrarily in half with only a 4-set floor, no minimum time span. That
        // let a client swapping to a fresh exercise a couple of sessions ago (small,
        // noisy sample) look identical to a genuine multi-week plateau. This is
        // anti-outlier (best-of-3-days baseline/current) and requires 2+ distinct
        // logged days before judging a trend at all — a freshly-swapped exercise
        // correctly lands in newBaselines instead of being flagged.
        getStrengthProgressSummary(userId),
      ]);
      const clientBodyweight = clientBwResult?.data?.bodyweight_kg || 0;
      // Every valid weigh-in as { date, weight, source }, oldest first, for the coach brief's weight sentence
      const briefWeighIns = (weightHistory || [])
        .filter(e => e.date && parseFloat(e.weight) > 0)
        .map(e => ({ date: new Date(e.date), weight: parseFloat(e.weight), source: e.source }))
        .filter(e => !isNaN(e.date.getTime()));
      const { data: rawSkipped } = skippedResult || {};
      const abandonedSessions = (abandonedResult?.data || []).map(s => {
        const daysAgo = Math.floor((Date.now() - new Date(s.started_at).getTime()) / (1000 * 60 * 60 * 24));
        return {
          id: s.id,
          startedAt: s.started_at,
          daysAgo,
          label: new Date(s.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        };
      });

      // How many sessions/week does their actual assigned program call for?
      // (avoids comparing adherence against a generic guessed target)
      let weeklyTarget = null;
      const activeProgramId = activeAssignmentResult?.data?.program_id;
      if (activeProgramId) {
        try {
          // An assignment without a week is on week 1, as the client's screen and the editor take it
          weeklyTarget = await loadWeeklySessionTarget(activeProgramId, { upToWeek: activeAssignmentResult.data.current_week ?? 1 });
        } catch (targetErr) {
          // Adherence is then shown without a target, rather than against a wrong one.
          console.error('Could not load the weekly session target:', targetErr);
        }
      }

      const { data: rawSets, error } = logsResult;
      const { data: rawSessionsData } = sessionsResult;

      // Filter sessions to those with completed logs OR completed_at
      const activeLogSessionIds = new Set((rawSets || []).map(s => s.session_id).filter(Boolean));
      const sessionsData = (rawSessionsData || []).filter(s => activeLogSessionIds.has(s.id) || s.completed_at);

      // Effort/wellness trend — mirrors the client-facing chart in EnhancedStats.jsx so
      // coaches see the same RPE data their client sees, not a separate computation.
      const rpeTrend = (rawSessionsData || [])
        .filter(s => s.rpe != null)
        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at))
        .slice(-12)
        .map(s => ({
          date: new Date(s.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          rpe: Number(s.rpe),
        }));
      const energyRatings = (rawSessionsData || []).filter(s => s.energy_rating != null).map(s => Number(s.energy_rating));
      const avgEnergyRating = energyRatings.length > 0
        ? Math.round((energyRatings.reduce((sum, v) => sum + v, 0) / energyRatings.length) * 10) / 10
        : null;

      // No longer building a program-week map; all grouping is by calendar week.

      if (error) {
        setLoadingAnalytics(false);
        return;
      }


      // Fetch exercise names/categories for all exercise_ids present in logs
      let exercisesLookup = {};
      if (rawSets && rawSets.length > 0) {
        const exerciseIds = [...new Set(rawSets.map(s => s.exercise_id).filter(Boolean))];
        if (exerciseIds.length > 0) {
          const { data: exData } = await supabase
            .from('exercises')
            .select('id, name, movement_category, secondary_muscles, is_bodyweight, is_assisted, bodyweight_multiplier')
            .in('id', exerciseIds);
          (exData || []).forEach(ex => { exercisesLookup[ex.id] = ex; });
        }
      }

      // Skipped exercises (using same exercisesLookup for names)
      const skippedCounts = {};
      (rawSkipped || []).forEach(log => {
        const name = exercisesLookup[log.exercise_id]?.name;
        if (name) skippedCounts[name] = (skippedCounts[name] || 0) + 1;
      });
      const skippedExercises = Object.entries(skippedCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);

      // Attach exercise info to each log row. Sort ascending by created_at:
      // the query is fetched DESC (so a row-cap keeps recent rows), but the
      // weekly bucketing, per-exercise PR running-max and first/second-half
      // trend splits below all assume chronological order.
      const completedSets = (rawSets || [])
        .map(set => ({
          ...set,
          exercises: exercisesLookup[set.exercise_id] || null,
        }))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      if (completedSets.length === 0) {
        setPerformanceAnalytics({
          weeklyData: [],
          totalVolume: 0,
          intensityTrend: 'stable',
          prFrequency: 0,
          performanceDrop: '0.0',
          alert: null,
          exerciseBreakdown: [],
          muscleGroupVolume: [],
          prBoard: [],
          sessionFrequency: [],
          avgSessionDuration: 0,
          dayOfWeekFrequency: [],
          needsAttention: strengthSummary?.needsAttention || [],
          strengthSummary: strengthSummary || null,
          skippedExercises: [],
          cardioStats: { totalSessions: 0, totalCardioMins: 0, cardioBreakdown: [] },
          weeklyTarget,
          weighIns: briefWeighIns,
          rpeTrend,
          avgEnergyRating,
          abandonedSessions,
        });
        setLoadingAnalytics(false);
        return;
      }

      // ── Helpers ──────────────────────────────────────────────────────────
      const getWeekKey = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return weekStart.toISOString().split('T')[0];
      };

      // ── Per-exercise breakdown ────────────────────────────────────────────
      const exerciseMap = {};
      completedSets.forEach(set => {
        const exerciseId = set.exercise_id;
        if (!exerciseId) return; // skip sets with no exercise reference
        const name = set.exercises?.name || null;
        if (!name) return; // skip sets where exercise name couldn't be resolved
        const category = set.exercises?.movement_category || 'Other';
        const secondaryRaw = set.exercises?.secondary_muscles || '';
        const secondaryMuscles = secondaryRaw ? secondaryRaw.split(',').map(m => m.trim()).filter(Boolean) : [];
        if (!exerciseMap[exerciseId]) {
          exerciseMap[exerciseId] = {
            name,
            category,
            secondaryMuscles,
            is_bodyweight: set.exercises?.is_bodyweight || false,
            is_assisted: set.exercises?.is_assisted || false,
            bodyweight_multiplier: set.exercises?.bodyweight_multiplier || 1.0,
            sets: [],
            rirSum: 0,
            rirCount: 0,
          };
        }
        exerciseMap[exerciseId].sets.push(set);
        if (set.rir != null) {
          exerciseMap[exerciseId].rirSum += parseFloat(set.rir);
          exerciseMap[exerciseId].rirCount += 1;
        }
      });

      // ── Weekly data (existing logic) ──────────────────────────────────────
      const weeklyData = {};
      const exercisePRs = {};

      completedSets.forEach(set => {
        // Group by calendar week (YYYY-MM-DD of week start)
        const weekKey = getWeekKey(set.created_at);
        if (weekKey == null) return;
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            week: weekKey,
            volume: 0,
            totalReps: 0,
            totalSets: 0,
            avgWeight: 0,
            weightSum: 0,
            weightCount: 0,
            prs: 0,
          };
        }

        const exercise = set.exercises;
        let weight = parseFloat(set.weight) || 0;
        if (exercise && clientBodyweight > 0) {
          if (exercise.is_bodyweight) {
            const multiplier = exercise.bodyweight_multiplier || 1.0;
            weight = clientBodyweight * multiplier;
          } else if (exercise.is_assisted) {
            weight = Math.max(0, clientBodyweight - weight);
          }
        }
        const reps = parseInt(set.reps) || 0;
        const volume = weight * reps;

        weeklyData[weekKey].volume += volume;
        weeklyData[weekKey].totalReps += reps;
        weeklyData[weekKey].totalSets += 1;

        if (weight > 0) {
          weeklyData[weekKey].weightSum += weight;
          weeklyData[weekKey].weightCount += 1;
        }

        const exerciseKey = `${set.program_exercise_id}`;
        if (!exercisePRs[exerciseKey]) {
          exercisePRs[exerciseKey] = { maxWeight: weight, maxVolume: volume };
        } else {
          if (weight > exercisePRs[exerciseKey].maxWeight) {
            exercisePRs[exerciseKey].maxWeight = weight;
            weeklyData[weekKey].prs += 1;
          }
          if (volume > exercisePRs[exerciseKey].maxVolume) {
            exercisePRs[exerciseKey].maxVolume = volume;
            weeklyData[weekKey].prs += 1;
          }
        }
      });

      // ── Bodyweight overlay — sort check-ins, then fill forward per workout week ──
      // getWeightHistory gives { date, weight } (it never gave check_in_date / bodyweight_kg, so this
      // overlay was always empty until 17 October 2026)
      const sortedBwHistory = (weightHistory || [])
        .filter(e => e.date && parseFloat(e.weight) > 0)
        .map(e => ({ date: new Date(e.date), weight: parseFloat(e.weight) }))
        .filter(e => !isNaN(e.date.getTime()))
        .sort((a, b) => a.date - b.date);

      // ── Convert weekly data to array ──────────────────────────────────────
      const weeksArray = Object.values(weeklyData)
        .map(week => ({
          ...week,
          avgWeight: week.weightCount > 0 ? Math.round(week.weightSum / week.weightCount) : 0,
          volumeKg: Math.round(week.volume),
        }))
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .map(week => {
          // week.week is always a YYYY-MM-DD calendar week string
          const weekDate = new Date(week.week);
          const weekEnd = weekDate ? new Date(weekDate.getTime() + 7 * 24 * 60 * 60 * 1000) : null;
          const match = weekEnd ? [...sortedBwHistory].reverse().find(e => e.date <= weekEnd) : null;
          return { ...week, bodyweight: match ? match.weight : null };
        });

      // ── Existing aggregate metrics ────────────────────────────────────────
      const totalVolume = weeksArray.reduce((sum, week) => sum + week.volumeKg, 0);

      const midpoint = Math.floor(weeksArray.length / 2);
      const firstHalfAvg = weeksArray.slice(0, midpoint).reduce((sum, w) => sum + w.avgWeight, 0) / (midpoint || 1);
      const secondHalfAvg = weeksArray.slice(midpoint).reduce((sum, w) => sum + w.avgWeight, 0) / ((weeksArray.length - midpoint) || 1);

      let intensityTrend = 'stable';
      const intensityChange = firstHalfAvg > 0 ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100 : 0;
      if (intensityChange > 5) intensityTrend = 'increasing';
      if (intensityChange < -5) intensityTrend = 'decreasing';

      const totalPRs = weeksArray.reduce((sum, week) => sum + week.prs, 0);
      const prFrequency = weeksArray.length > 0 ? (totalPRs / weeksArray.length).toFixed(1) : 0;

      let performanceDrop = 0;
      let alert = null;
      if (weeksArray.length >= 4) {
        const lastTwoWeeks = weeksArray.slice(-2);
        const prevTwoWeeks = weeksArray.slice(-4, -2);
        const lastAvgVolume = lastTwoWeeks.reduce((sum, w) => sum + w.volumeKg, 0) / 2;
        const prevAvgVolume = prevTwoWeeks.reduce((sum, w) => sum + w.volumeKg, 0) / 2;
        const lastAvgWeight = lastTwoWeeks.reduce((sum, w) => sum + w.avgWeight, 0) / 2;
        const prevAvgWeight = prevTwoWeeks.reduce((sum, w) => sum + w.avgWeight, 0) / 2;
        const volumeChange = prevAvgVolume > 0 ? ((lastAvgVolume - prevAvgVolume) / prevAvgVolume) * 100 : 0;
        const weightChange = prevAvgWeight > 0 ? ((lastAvgWeight - prevAvgWeight) / prevAvgWeight) * 100 : 0;
        // Signed (positive = improvement, negative = drop)
        performanceDrop = weightChange;
        if (volumeChange > 10 && weightChange < -5) {
          alert = {
            type: 'overtraining',
            message: `Volume increased ${volumeChange.toFixed(0)}% but weight dropped ${Math.abs(weightChange).toFixed(0)}%. Possible overtraining.`,
            severity: 'warning',
          };
        } else if (weightChange < -10) {
          alert = {
            type: 'performance_drop',
            message: `Performance dropped ${Math.abs(weightChange).toFixed(0)}% in last 2 weeks.`,
            severity: 'danger',
          };
        }
      }

      // ── PR Board — top record per exercise (uses effective weight) ────────
      const prBoard = Object.values(exerciseMap)
        .map(ex => {
          let maxEffWeight = 0, maxWeightDate = null, repsAtMax = 0;
          ex.sets.forEach(set => {
            const effW = getEffectiveWeight(set.weight, ex, clientBodyweight);
            if (effW > maxEffWeight) {
              maxEffWeight = effW;
              maxWeightDate = set.created_at;
              repsAtMax = parseInt(set.reps) || 0;
            }
          });
          return { name: ex.name, maxWeight: Math.round(maxEffWeight), maxWeightDate, reps: repsAtMax };
        })
        .filter(e => e.maxWeight > 0)
        .sort((a, b) => b.maxWeight - a.maxWeight)
        .slice(0, 10);

      // ── Muscle group volume split (resistance only, primary + secondary) ─────
      // Build inverted lookup: specific muscle name → primary category
      const secondaryToCategory = {};
      Object.entries(SECONDARY_MUSCLES).forEach(([cat, muscles]) => {
        muscles.forEach(m => { secondaryToCategory[m] = cat; });
      });

      const muscleGroupVolumeMap = {};
      const muscleGroupSetsMap = {};
      Object.values(exerciseMap).forEach(ex => {
        if (ex.category === 'Cardio' || ex.category === 'Stretch') return;
        const vol = ex.sets.reduce((s, set) =>
          s + getSetVolume(set.weight, set.reps, ex, clientBodyweight)
        , 0);
        if (vol === 0) return;
        // Primary category — full credit
        muscleGroupVolumeMap[ex.category] = (muscleGroupVolumeMap[ex.category] || 0) + vol;
        muscleGroupSetsMap[ex.category] = (muscleGroupSetsMap[ex.category] || 0) + ex.sets.length;
        // Secondary muscles — 30% credit, mapped to their primary category
        ex.secondaryMuscles.forEach(muscle => {
          const cat = secondaryToCategory[muscle];
          if (!cat || cat === ex.category || cat === 'Cardio' || cat === 'Stretch') return;
          muscleGroupVolumeMap[cat] = (muscleGroupVolumeMap[cat] || 0) + vol * 0.3;
          muscleGroupSetsMap[cat] = (muscleGroupSetsMap[cat] || 0) + Math.round(ex.sets.length * 0.3);
        });
      });
      const muscleGroupVolume = Object.entries(muscleGroupVolumeMap)
        .map(([name, value]) => ({ name, value: Math.round(value), sets: muscleGroupSetsMap[name] || 0 }))
        .sort((a, b) => b.value - a.value);

      // ── Exercise breakdown (for Top Exercises table) ──────────────────────
      const exerciseBreakdown = Object.values(exerciseMap)
        .map(ex => {
          const totalVol = ex.sets.reduce((s, set) => {
            return s + getSetVolume(set.weight, set.reps, ex, clientBodyweight);
          }, 0);
          // Use effective weights for max and trend so bodyweight/assisted are correct
          const effectiveWeights = ex.sets.map(s => getEffectiveWeight(s.weight, ex, clientBodyweight));
          const maxWeight = Math.round(Math.max(...effectiveWeights, 0));
          const midpt = Math.floor(ex.sets.length / 2);
          const firstHalfWeights = effectiveWeights.slice(0, midpt || 1);
          const secondHalfWeights = effectiveWeights.slice(midpt);
          const firstAvg = firstHalfWeights.reduce((s, w) => s + w, 0) / (firstHalfWeights.length || 1);
          const secondAvg = secondHalfWeights.reduce((s, w) => s + w, 0) / (secondHalfWeights.length || 1);
          const trendPct = firstAvg > 0 ? ((secondAvg - firstAvg) / firstAvg) * 100 : 0;
          const trend = trendPct > 5 ? 'up' : trendPct < -5 ? 'down' : 'stable';
          const avgRir = ex.rirCount > 0 ? (ex.rirSum / ex.rirCount).toFixed(1) : null;
          return { name: ex.name, category: ex.category, maxWeight, totalVolume: Math.round(totalVol), trend, sessions: ex.sets.length, avgRir };
        })
        .sort((a, b) => b.totalVolume - a.totalVolume);

      // ── Session frequency per week ────────────────────────────────────────
      // Count unique training DAYS per week from workout_logs — same source as
      // the Training Calendar — so both charts always agree.
      const uniqueDaysPerWeek = {};
      (completedSets || []).forEach(log => {
        const wk = getWeekKey(log.created_at);
        if (wk == null) return;
        if (!uniqueDaysPerWeek[wk]) uniqueDaysPerWeek[wk] = new Set();
        const d = new Date(log.created_at);
        const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        uniqueDaysPerWeek[wk].add(localDate);
      });
      const sessionFrequency = Object.entries(uniqueDaysPerWeek)
        .map(([week, days]) => ({ week, sessions: days.size }))
        .sort((a, b) => new Date(a.week) - new Date(b.week));

      // ── Avg session duration (minutes) — use log timestamps for accuracy ──
      const sessionLogTsMap = {};
      (rawSets || []).forEach(log => {
        if (!log.session_id || !log.created_at) return;
        if (!sessionLogTsMap[log.session_id]) sessionLogTsMap[log.session_id] = [];
        sessionLogTsMap[log.session_id].push(new Date(log.created_at).getTime());
      });
      const durationsMinutes = (sessionsData || []).map(s => {
        const ts = sessionLogTsMap[s.id];
        if (ts && ts.length >= 2) {
          return Math.round((Math.max(...ts) - Math.min(...ts)) / 60000);
        }
        if (s.started_at && s.completed_at) {
          return Math.min(
            Math.round((new Date(s.completed_at) - new Date(s.started_at)) / 60000),
            180
          );
        }
        return null;
      }).filter(d => d != null && d > 0);
      const avgSessionDuration = durationsMinutes.length > 0
        ? Math.round(durationsMinutes.reduce((a, b) => a + b, 0) / durationsMinutes.length)
        : 0;

      // ── Day-of-week frequency (unique training dates, not raw session count) ─
      const dowCounts = [0, 0, 0, 0, 0, 0, 0];
      const trainingDatesSeen = new Set();
      (sessionsData || []).forEach(s => {
        if (!s.started_at) return;
        const d = new Date(s.started_at);
        const localDateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        if (!trainingDatesSeen.has(localDateKey)) {
          trainingDatesSeen.add(localDateKey);
          dowCounts[d.getDay()]++;
        }
      });
      const dayOfWeekFrequency = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        .map((day, i) => ({ day, count: dowCounts[i] }));

      // ── Cardio stats ─────────────────────────────────────────────────────
      const intensityScore = { low: 1, moderate: 2, high: 3, maximum: 4 };
      const cardioSets = completedSets.filter(s => s.exercise_type === 'cardio');
      const cardioExerciseMap = {};
      cardioSets.forEach(s => {
        const name = s.exercise_name || 'Unknown';
        if (!cardioExerciseMap[name]) cardioExerciseMap[name] = [];
        cardioExerciseMap[name].push(s);
      });
      const cardioBreakdown = Object.entries(cardioExerciseMap).map(([name, sessions]) => {
        const sorted = [...sessions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const avgDurationMins = sorted.length > 0
          ? Math.round(sorted.reduce((s, x) => s + (x.duration_seconds || 0), 0) / sorted.length / 60)
          : 0;
        const intensityCounts = { low: 0, moderate: 0, high: 0, maximum: 0 };
        sorted.forEach(x => { if (x.intensity) intensityCounts[x.intensity]++; });
        const dominantIntensity = Object.entries(intensityCounts).sort((a, b) => b[1] - a[1]).find(([, v]) => v > 0)?.[0] || null;
        const totalDistanceKm = sorted.reduce((s, x) => s + (parseFloat(x.distance_km) || 0), 0);
        const levelSamples = sorted.filter(x => x.resistance_level);
        const avgLevel = levelSamples.length > 0
          ? Math.round((levelSamples.reduce((s, x) => s + parseFloat(x.resistance_level), 0) / levelSamples.length) * 10) / 10
          : null;
        const totalCalories = sorted.reduce((s, x) => s + (parseInt(x.calories) || 0), 0);
        // Weekly intensity trend (score 1-4)
        const weeklyScores = {};
        sorted.forEach(x => {
          const wk = getWeekKey(x.created_at);
          if (!wk || !x.intensity) return;
          if (!weeklyScores[wk]) weeklyScores[wk] = { sum: 0, count: 0 };
          weeklyScores[wk].sum += intensityScore[x.intensity] || 0;
          weeklyScores[wk].count += 1;
        });
        const intensityTrendData = Object.entries(weeklyScores)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .map(([week, d]) => ({ week, avg: Math.round((d.sum / d.count) * 10) / 10 }));
        return {
          name,
          totalSessions: sorted.length,
          avgDurationMins,
          dominantIntensity,
          intensityCounts,
          totalDistanceKm: Math.round(totalDistanceKm * 10) / 10,
          avgLevel,
          totalCalories,
          intensityTrendData,
          recentSessions: sorted.slice(-5).reverse().map(x => ({
            date: x.created_at,
            intensity: x.intensity,
            durationMins: x.duration_seconds ? Math.round(x.duration_seconds / 60) : null,
            distanceKm: x.distance_km ? parseFloat(x.distance_km) : null,
            level: x.resistance_level ? parseFloat(x.resistance_level) : null,
            calories: x.calories ? parseInt(x.calories) : null,
            notes: x.cardio_notes || null,
          })),
        };
      }).sort((a, b) => b.totalSessions - a.totalSessions);
      const totalCardioMins = Math.round(cardioSets.reduce((s, x) => s + (x.duration_seconds || 0), 0) / 60);
      const cardioStats = {
        totalSessions: cardioSets.length,
        totalCardioMins,
        cardioBreakdown,
      };

      // ── Days since most recent PR (for dynamic subtitle) ─────────────────
      const mostRecentPRDate = prBoard.reduce((latest, pr) => {
        if (!pr.maxWeightDate) return latest;
        const d = new Date(pr.maxWeightDate);
        return d > latest ? d : latest;
      }, new Date(0));
      const daysSinceLastPR = mostRecentPRDate.getTime() > 0
        ? Math.floor((Date.now() - mostRecentPRDate.getTime()) / (1000 * 60 * 60 * 24))
        : null;

      setPerformanceAnalytics({
        weeklyData: weeksArray,
        totalVolume,
        intensityTrend,
        prFrequency,
        performanceDrop: performanceDrop.toFixed(1),
        alert,
        exerciseBreakdown,
        muscleGroupVolume,
        prBoard,
        sessionFrequency,
        daysSinceLastPR,
        avgSessionDuration,
        dayOfWeekFrequency,
        needsAttention: strengthSummary?.needsAttention || [],
        strengthSummary: strengthSummary || null,
        skippedExercises,
        cardioStats,
        weeklyTarget,
        weighIns: briefWeighIns,
        rpeTrend,
        avgEnergyRating,
        abandonedSessions,
      });
    } catch (err) {
      console.error('Could not load performance analytics:', err);
      toastError('Could not load performance analytics. Please try again.');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadProgramHistory = async (userId) => {
    setLoadingProgramHistory(true);
    try {
      // Step 1: fetch all assignments for this user
      const { data: assignments, error } = await supabase
        .from('program_assignments')
        .select('id, program_id, is_active, assigned_at, ended_at, current_week')
        .eq('user_id', userId)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      // Step 2: fetch program details for any program_ids not already in local state
      const localProgramIds = new Set(programs.map(p => p.id));
      const missingIds = [...new Set((assignments || []).map(a => a.program_id))]
        .filter(id => id && !localProgramIds.has(id));

      let extraPrograms = [];
      if (missingIds.length > 0) {
        const { data: fetched, error: fetchedErr } = await supabase
          .from('programs')
          .select('id, name, description')
          .in('id', missingIds);
        // A failed lookup and a program this coach may not read both end as "Unknown Program"
        // below; only the first is worth a trace.
        if (fetchedErr) console.error('Could not load program names for the history:', fetchedErr);
        extraPrograms = fetched || [];
      }

      const allPrograms = [...programs, ...extraPrograms];

      const enriched = (assignments || []).map(a => ({
        ...a,
        program: allPrograms.find(p => p.id === a.program_id) || { id: a.program_id, name: 'Unknown Program', description: '' },
      }));

      setProgramHistory(enriched);
    } catch (err) {
      console.error('Could not load program history:', err);
      toastError('Could not load program history. Please try again.');
      setProgramHistory([]);
    } finally {
      setLoadingProgramHistory(false);
    }
  };

  const loadClientFlags = async (clientUserId) => {
    const { data } = await supabase
      .from('exercise_flags')
      .select('*')
      .eq('user_id', clientUserId)
      .is('resolved_at', null)
      .order('flagged_at', { ascending: false });
    setClientFlags(data || []);
  };

  // Exercises this client swapped mid-workout. Read-only for the coach (migration 063).
  const loadSwapsForClient = async (clientUserId) => {
    swapsRequestRef.current = clientUserId;
    const rows = await loadClientSwaps(clientUserId, swapHistorySince());
    // Another client was opened while this was loading: its answer must not land on their screen.
    if (swapsRequestRef.current !== clientUserId) return;
    setClientSwaps(rows === null ? null : summariseSwaps(rows));
  };

  // The client's daily calorie and protein totals for the coach brief (migration 100); null when there is
  // nothing to say, which leaves food out of the brief.
  const loadFoodForClient = async (clientUserId) => {
    foodRequestRef.current = clientUserId;
    const summary = await loadClientFoodSummary(clientUserId);
    // Another client was opened while this was loading: its answer must not land on their brief.
    if (foodRequestRef.current !== clientUserId) return;
    setClientFood(summary);
  };

  const handleResolveFlag = async (flagId) => {
    const flag = clientFlags.find(f => f.id === flagId);
    const { data: { user } } = await supabase.auth.getUser();

    // If a substitute was selected, swap the exercise directly in the program
    if (flagSubSelected && flag?.program_exercise_id) {
      await supabase
        .from('program_exercises')
        .update({ exercise_id: flagSubSelected.id })
        .eq('id', flag.program_exercise_id);
    }

    const { error } = await supabase
      .from('exercise_flags')
      .update({
        resolved_at: new Date().toISOString(),
        resolved_by: user.id,
        resolution_notes: resolveNote.trim() || null
      })
      .eq('id', flagId);
    if (error) {
      console.error('handleResolveFlag error:', error);
      toastError(`Could not resolve flag: ${error.message}`);
      return;
    }
    if (!error) {
      setClientFlags(prev => prev.filter(f => f.id !== flagId));
      setClientFlagCounts(prev => {
        const uid = selectedClient.user_id;
        const updated = (prev[uid] || 1) - 1;
        return { ...prev, [uid]: updated > 0 ? updated : 0 };
      });
      setResolvingFlag(null);
      setResolveNote('');
      setFlagSubSelected(null);
      setFlagSubSearch('');
      setFlagSubDropdownOpen(false);
    }
  };

  const saveSessionNote = async (sessionId) => {
    setSessionNoteSaving(true);
    const { error } = await supabase
      .from('workout_sessions')
      .update({ coach_notes: sessionNoteText.trim() || null })
      .eq('id', sessionId);
    setSessionNoteSaving(false);
    if (error) { toastError('Could not save note'); return; }
    // Update local recentSessions data
    const uid = selectedClient?.user_id;
    if (uid && clientMetrics[uid]) {
      const updated = clientMetrics[uid].recentSessions.map(s =>
        s.id === sessionId ? { ...s, coach_notes: sessionNoteText.trim() || null } : s
      );
      setClientMetrics(prev => ({
        ...prev,
        [uid]: { ...prev[uid], recentSessions: updated },
      }));
    }
    setSessionNoteEditing(null);
    setSessionNoteText('');
    toastSuccess('Feedback saved');
    // No push notification — the dashboard's coach-feedback banner already
    // surfaces this the moment the client opens the app.
  };

  const loadClientCoachNotes = async (userId) => {
    try {
      setClientCoachNotes(await getCoachNotes(userId));
    } catch {
      setClientCoachNotes([]);
    }
  };

  const sendCoachNote = async (rawMessage, { onSuccess, setSending = setSendingNote, checkInId = null } = {}) => {
    const message = (rawMessage || '').trim();
    if (!message || !selectedClient) return;
    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not logged in');
      const clientAuthId = selectedClient?.auth_user_id || selectedClient?.user_id;

      await addCoachNote(clientAuthId, user.id, message, checkInId);

      toastSuccess(checkInId ? 'Review saved' : 'Note sent to client');
      loadClientCoachNotes(clientAuthId);
      onSuccess?.();
      // No push notification — the dashboard's "MESSAGE FROM YOUR COACH"
      // banner already surfaces this the moment the client opens the app.
    } catch (err) {
      toastError('Could not send note: ' + (err.message || 'Unknown error'));
    } finally {
      setSending(false);
    }
  };

  // Sending the Coach Brief: just before it goes, ask again whether the client still shares food totals. If
  // they have stopped, a note that carries them is not sent: the generated sentence is taken out and the coach
  // reads the note once more; an edited one is held until the coach edits it again or sends it as it is.
  const sendBriefingNote = async (rawText) => {
    const text = (rawText || '').trim();
    if (!text || !selectedClient) return;
    const foodSentence = describeFood(clientFood) || withdrawnFoodSentenceRef.current;
    let stillShared = true;
    if (foodSentence) {
      setSendingNote(true);
      try {
        stillShared = (await loadClientFoodSummary(selectedClient.user_id)) !== null;
      } finally {
        setSendingNote(false);
      }
    }
    const verdict = guardFoodBeforeSend({ text, foodSentence, stillShared, confirmedText: foodConfirmedTextRef.current });
    if (verdict.action === 'send') {
      foodConfirmedTextRef.current = null;
      withdrawnFoodSentenceRef.current = '';
      return sendCoachNote(verdict.text);
    }
    // Not shared any more: Regenerate must not put it back, and what the note still holds stays checked
    setClientFood(null);
    if (verdict.action === 'removed') {
      withdrawnFoodSentenceRef.current = '';
      // Not regenerated over the coach's text: it is now theirs to read and send
      lastBriefingRef.current = '';
      setBriefingText(verdict.text);
      toastError('This client no longer shares food totals, so that sentence was taken out. Read the note, then send it again.');
    } else {
      foodConfirmedTextRef.current = text;
      withdrawnFoodSentenceRef.current = foodSentence;
      toastError('This client no longer shares food totals, and this note may still contain them. Edit it or press Regenerate; or press Send again to send it as it is.');
    }
  };

  const loadClientMessages = async (clientAuthId) => {
    setLoadingMessages(true);
    try {
      setClientMessages(await getMessages(clientAuthId));
    } catch (err) {
      toastError('Could not load messages: ' + (err.message || 'Unknown error'));
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendClientMessage = async (text) => {
    if (!selectedClient) return;
    const clientAuthId = selectedClient?.auth_user_id || selectedClient?.user_id;
    setSendingMessage(true);
    try {
      await sendMessageApi(clientAuthId, 'coach', text);
      await loadClientMessages(clientAuthId);
      return true;
    } catch (err) {
      toastError('Could not send message, your text is still there: ' + (err.message || 'Unknown error'));
      return false;
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    if (!selectedClient || !pendingTabRef.current) return;
    const tab = pendingTabRef.current;
    pendingTabRef.current = null;
    if (tab === 'messages') openMessagesTab();
    else setActiveTab(tab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  const openMessagesTab = async () => {
    setActiveTab('messages');
    if (!selectedClient) return;
    const clientAuthId = selectedClient?.auth_user_id || selectedClient?.user_id;
    await loadClientMessages(clientAuthId);
    try {
      await markMessagesRead(clientAuthId, 'coach');
      setClientUnreadMessageCounts(prev => {
        const next = { ...prev };
        delete next[selectedClient.user_id];
        return next;
      });
    } catch (err) {
      console.error('Failed to mark messages read:', err);
    }
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setEditingProgram(null);
    setProgramDetails(null);
    setActiveTab('performance');
    setShowAssignPanel(false);
    setSelectedProgram('');
    setClientFlags([]);
    setClientSwaps([]);
    setClientFood(null);
    foodConfirmedTextRef.current = null;
    withdrawnFoodSentenceRef.current = '';
    setClientMessages([]);
    setResolvingFlag(null);
    setResolveNote('');
    setShowDeleteSession(false);
    setShowAdvanceWeek(false);
    setShowOverflowMenu(false);
    setMoveTarget({});
    loadPerformanceAnalytics(client.user_id, timeRange);
    loadProgramHistory(client.user_id);
    loadClientFlags(client.user_id);
    loadSwapsForClient(client.user_id);
    loadFoodForClient(client.user_id);
    loadClientCoachNotes(client.auth_user_id || client.user_id);
  };

  // Format date for display
  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  useEffect(() => {
    if (editingProgramRef.current !== (editingProgram?.id ?? null)) {
      editingProgramRef.current = editingProgram?.id ?? null;
      if (!editingProgram) programLoadRef.current += 1; // closed: a read still running is dropped
    }
  }, [editingProgram]);

  const viewProgram = async (program) => {
    setEditingProgram(program);
    setExerciseAvgs({});
    // The previous program's tree must not stay editable while this one loads (R3)
    setProgramDetails(null);
    loadProgramDetails(program.id);
    const ticket = programLoadRef.current;

    // Fetch 4-week workout averages for this client
    if (selectedClient) {
      const since = new Date();
      since.setDate(since.getDate() - 28);
      const { data: logs } = await supabase
        .from('workout_logs')
        .select('exercise_id, weight, reps')
        .eq('user_id', selectedClient.user_id)
        .eq('completed', true)
        .gte('created_at', since.toISOString());

      if (ticket !== programLoadRef.current) return; // another program was opened meanwhile
      if (logs && logs.length > 0) {
        const map = {};
        logs.forEach(({ exercise_id, weight, reps }) => {
          if (!exercise_id || !weight) return;
          if (!map[exercise_id]) map[exercise_id] = { totalWeight: 0, totalReps: 0, count: 0 };
          map[exercise_id].totalWeight += parseFloat(weight);
          map[exercise_id].totalReps += parseInt(reps) || 0;
          map[exercise_id].count += 1;
        });
        const avgs = {};
        Object.entries(map).forEach(([id, v]) => {
          avgs[id] = {
            avgWeight: (v.totalWeight / v.count).toFixed(1),
            avgReps: Math.round(v.totalReps / v.count),
          };
        });
        setExerciseAvgs(avgs);
      }
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Loading clients...</p>
      </div>
    );
  }

  // Client Detail View
  if (selectedClient) {

    return (
      <div style={styles.container}>
        <ClientDetailHeader
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          setDeleteDialog={setDeleteDialog}
          clientGlp1={clientGlp1}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          loadProgramHistory={loadProgramHistory}
          setShowAssignPanel={setShowAssignPanel}
          openMessagesTab={openMessagesTab}
          clientUnreadMessageCounts={clientUnreadMessageCounts}
        />

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <ClientPerformanceTab
            selectedClient={selectedClient}
            performanceAnalytics={performanceAnalytics}
            clientSwaps={clientSwaps}
            clientFood={clientFood}
            loadingAnalytics={loadingAnalytics}
            dateSpan={analyticsDateSpan}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            clientCoachNotes={clientCoachNotes}
            briefingText={briefingText}
            setBriefingText={setBriefingText}
            sendingNote={sendingNote}
            sendCoachNote={sendBriefingNote}
            activePieIndex={activePieIndex}
            setActivePieIndex={setActivePieIndex}
            clientMetrics={clientMetrics}
            sessionNoteEditing={sessionNoteEditing}
            setSessionNoteEditing={setSessionNoteEditing}
            sessionNoteText={sessionNoteText}
            setSessionNoteText={setSessionNoteText}
            sessionNoteSaving={sessionNoteSaving}
            saveSessionNote={saveSessionNote}
          />
        )}


        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <ClientProgramsTab
            selectedClient={selectedClient}
            programs={programs}
            exerciseLibrary={exerciseLibrary}
            assigningTo={assigningTo}
            selectedProgram={selectedProgram}
            setSelectedProgram={setSelectedProgram}
            editingProgram={editingProgram}
            setEditingProgram={setEditingProgram}
            programDetails={programDetails}
            setProgramDetails={setProgramDetails}
            swappingExercise={swappingExercise}
            setSwappingExercise={setSwappingExercise}
            pendingSwapEx={pendingSwapEx}
            setPendingSwapEx={setPendingSwapEx}
            swapReason={swapReason}
            setSwapReason={setSwapReason}
            setConfirmDialog={setConfirmDialog}
            showOverflowMenu={showOverflowMenu}
            setShowOverflowMenu={setShowOverflowMenu}
            programHistory={programHistory}
            showAssignPanel={showAssignPanel}
            setShowAssignPanel={setShowAssignPanel}
            showAdvanceWeek={showAdvanceWeek}
            setShowAdvanceWeek={setShowAdvanceWeek}
            advanceWeekTarget={advanceWeekTarget}
            setAdvanceWeekTarget={setAdvanceWeekTarget}
            advancingWeek={advancingWeek}
            availableWeeks={availableWeeks}
            generatingNextWeek={generatingNextWeek}
            loadingProgramHistory={loadingProgramHistory}
            exerciseAvgs={exerciseAvgs}
            setExerciseAvgs={setExerciseAvgs}
            swapSearch={swapSearch}
            setSwapSearch={setSwapSearch}
            addingExercise={addingExercise}
            setAddingExercise={setAddingExercise}
            addExerciseSearch={addExerciseSearch}
            setAddExerciseSearch={setAddExerciseSearch}
            showSwapAll={showSwapAll}
            setShowSwapAll={setShowSwapAll}
            clientFlags={clientFlags}
            clientSwaps={clientSwaps}
            resolvingFlag={resolvingFlag}
            setResolvingFlag={setResolvingFlag}
            resolveNote={resolveNote}
            setResolveNote={setResolveNote}
            flagSubSearch={flagSubSearch}
            setFlagSubSearch={setFlagSubSearch}
            flagSubSelected={flagSubSelected}
            setFlagSubSelected={setFlagSubSelected}
            flagSubDropdownOpen={flagSubDropdownOpen}
            setFlagSubDropdownOpen={setFlagSubDropdownOpen}
            showDeleteSession={showDeleteSession}
            setShowDeleteSession={setShowDeleteSession}
            clientSessions={clientSessions}
            loadingSessions={loadingSessions}
            deletingSession={deletingSession}
            movingSession={movingSession}
            programDays={programDays}
            moveTarget={moveTarget}
            setMoveTarget={setMoveTarget}
            handleResolveFlag={handleResolveFlag}
            markAsReviewed={markAsReviewed}
            viewProgram={viewProgram}
            openAdvanceWeek={openAdvanceWeek}
            loadClientSessions={loadClientSessions}
            loadProgramDays={loadProgramDays}
            handleDeleteSession={handleDeleteSession}
            handleMoveSession={handleMoveSession}
            handleAdvanceWeek={handleAdvanceWeek}
            handleGenerateNextWeek={handleGenerateNextWeek}
            getSuggestedExercises={getSuggestedExercises}
            programLoadFailed={programLoadFailed}
            editorWeek={editorWeek}
            onViewWeek={(w) => editingProgram && loadProgramDetails(editingProgram.id, { useCache: false, week: w })}
            addDay={addDay}
            addDaySaving={addDaySaving}
            retryProgramLoad={() => editingProgram && loadProgramDetails(editingProgram.id, { useCache: false })}
            updateBlockItem={updateBlockItem}
            updateBlockRest={updateBlockRest}
            swapBlockExercise={swapBlockExercise}
            swapSaving={swapSaving}
            deleteBlockItem={deleteBlockItem}
            updateExercise={updateExercise}
            swapExercise={swapExercise}
            deleteProgramExercise={deleteProgramExercise}
            addExerciseToDay={addExerciseToDay}
            addExerciseSaving={addExerciseSaving}
            assignProgram={assignProgram}
            getAssignableProgramGroups={getAssignableProgramGroups}
          />
        )}


        {/* Check-Ins Tab */}
        {activeTab === 'checkins' && (
          <ClientCheckInsTab
            key={selectedClient?.user_id}
            selectedClient={selectedClient}
            clientStartingWeight={clientStartingWeight}
            clientOnboardingPhotos={clientOnboardingPhotos}
            clientWeightHistory={clientWeightHistory}
            clientCoachNotes={clientCoachNotes}
            clientCheckIns={clientCheckIns}
            sendCoachNote={sendCoachNote}
          />
        )}


        {/* ── Nutrition Tab ─────────────────────────────────────── */}
        {activeTab === 'nutrition' && (
          <ClientNutritionTab
            key={selectedClient?.user_id}
            selectedClient={selectedClient}
            clientNutritionData={clientNutritionData}
            nutritionStatus={nutritionStatus}
            onRetry={() => setNutritionRetry(n => n + 1)}
            clientWeightHistory={clientWeightHistory}
          />
        )}

        {/* ── Messages Tab ──────────────────────────────────────── */}
        {activeTab === 'messages' && (
          <ClientMessagesTab
            key={selectedClient?.user_id}
            selectedClient={selectedClient}
            messages={clientMessages}
            loadingMessages={loadingMessages}
            sendingMessage={sendingMessage}
            onSend={sendClientMessage}
            onRefresh={openMessagesTab}
          />
        )}


        {/* Remove Exercise Confirmation Dialog */}
        <ConfirmDialog
          isOpen={removeExerciseDialog.isOpen}
          onClose={() => setRemoveExerciseDialog({ isOpen: false, action: null })}
          onConfirm={async () => {
            if (removingExercise) return; // a second tap while the first removal runs (R6)
            setRemovingExercise(true);
            try {
              await removeExerciseDialog.action?.();
            } finally {
              setRemovingExercise(false);
              setRemoveExerciseDialog({ isOpen: false, action: null });
            }
          }}
          loading={removingExercise}
          title="Remove Exercise"
          message="Remove this exercise from the program? This will also remove it from all future weeks."
          confirmText="Remove"
          variant="danger"
        />

        {/* Delete Client Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, client: null })}
          onConfirm={deleteClient}
          title="Delete Client"
          message={`Are you sure you want to delete ${deleteDialog.client?.full_name}? This will remove all their data including programs, workout sessions, and photos. This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleteLoading}
        />

        {/* Unassign Program Confirmation Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, programId: null, userId: null })}
          onConfirm={handleConfirmUnassign}
          title="Unassign Program"
          message="Are you sure you want to unassign this program from the client?"
          confirmText="Unassign"
          variant="danger"
          loading={unassignLoading}
        />
      </div>
    );
  }

  // Clients List View
  return (
    <ClientListView
      onBack={onBack}
      clients={visibleClients}
      programs={programs}
      loading={loading}
      error={error}
      clientFlagCounts={clientFlagCounts}
      clientUnreadMessageCounts={clientUnreadMessageCounts}
      coaches={coaches}
      getClientPrograms={getClientPrograms}
      selectClient={selectClient}
      hasMoreClients={hasMoreClients}
      loadingMoreClients={loadingMoreClients}
      onLoadMoreClients={loadMoreClients}
      clientTotalCount={clientTotalCount}
      clientTab={clientTab}
      onChangeClientTab={setClientTab}
      activeClientCount={clients.length - inactiveClientCount}
      inactiveClientCount={inactiveClientCount}
      archivingClient={archivingClient}
      onArchiveClient={handleArchiveClient}
      getInactiveReason={getInactiveReason}
      needs={needs}
      onOpenNeedsClient={(needClient, tab) => {
        const match = clients.find(c => c.user_id === needClient.auth_user_id);
        if (!match) { toastError(`${needClient.name || 'This client'} is not in the list loaded here yet. Use Load More.`); return; }
        pendingTabRef.current = tab || null;
        selectClient(match);
      }}
    />
  );
};

export default ClientManagement;
